// Main application file

import { inject } from '@vercel/analytics';

import { initDB, saveShapefile, getShapefileMetadata, getShapefile, clearAllShapefiles, resetDatabase } from './db.js';
import { loadShapefile, setupDragAndDrop, extractMetadata, detectShapefileComponents, createVirtualZip } from './shapefile-loader.js';
import { loadCSV } from './csv-loader.js';
import { loadExcel } from './excel-loader.js';
import { loadGeoJSON } from './geojson-loader.js';
import { loadKML } from './kml-loader.js';
import { loadGPX } from './gpx-loader.js';
import { loadPhoto, loadPhotos, isImageFile, extractPhotosFromZip } from './photo-loader.js';
import { initMap, removeHighlight, resizeMap, highlightFeature, toggleEditMode, isEditMode, undoEdit, redoEdit, toggleDarkMap, getCurrentEditLayerId, getMap, updateEditControlButtons, getLayerSourceData, startDrawingMode, stopDrawingMode, saveDrawingChanges, isDrawingMode, updateDrawButtonState, resetEditHistory } from './map.js';
import {
    initializeLayerManager,
    addLayer,
    removeLayer,
    removeAllLayers,
    toggleLayerVisibility,
    setCurrentLayer,
    getAllLayers,
    getLayerCount,
    getLayer,
    getFeature,
    getCurrentLayer,
    reorderLayers
} from './modules/layerManager.js';
import {
    displayAttributeTable,
    showTable,
    hideTable,
    clearTable,
    highlightRow,
    clearRowHighlight
} from './modules/tableManager.js';
import {
    initializeTabManager,
    switchToLayerTab,
    getActiveLayerId
} from './modules/tabManager.js';
import { exportLayer } from './modules/exportUtils.js';
import { initializeTutorial, startTutorial } from './modules/tutorialManager.js';
import AI_SERVICE from './services/aiService.js';
import {
    initializeClerk,
    getCurrentUser,
    getUserId,
    isAuthenticated,
    onAuthStateChange,
    getClerk,
    openSignIn,
    openSignUp,
    openUserProfile,
    signOut
} from './auth/clerkConfig.js';

// DOM Elements
const fileInput = document.getElementById('file-input');
const browseBtn = document.getElementById('browse-btn');
const fileInfo = document.getElementById('file-info');
const fileDetails = document.getElementById('file-details');
const statusDiv = document.getElementById('status');
const progressContainer = document.getElementById('progress-container');
const progressFill = document.getElementById('progress-fill');
const progressText = document.getElementById('progress-text');
const mapContainer = document.getElementById('map-container');
const attributeTableContainer = document.getElementById('attribute-table-container');
const tableHead = document.getElementById('table-head');
const tableBody = document.getElementById('table-body');
const tableRecordCount = document.getElementById('table-record-count');
const tableTabs = document.getElementById('table-tabs');
const tableScrollContainer = document.querySelector('.table-scroll');
const showTableBtn = document.getElementById('show-table-btn');
const closeTableBtn = document.getElementById('close-table-btn');
const darkMapToggle = document.getElementById('dark-map-toggle');
const previewPlayOverlay = document.getElementById('preview-play-overlay');
const previewPlayBtn = document.getElementById('preview-play-btn');
const previewBackBtn = document.getElementById('preview-back-btn');
const previewForwardBtn = document.getElementById('preview-forward-btn');
const previewCloseBtn = document.getElementById('preview-close-btn');
const legendContent = document.getElementById('legend-content');
const editControlsPopup = document.getElementById('edit-controls-popup');
const editUndoBtn = document.getElementById('edit-undo-btn');
const editRedoBtn = document.getElementById('edit-redo-btn');
const editSaveBtn = document.getElementById('edit-save-btn');
const closeEditPopupBtn = document.getElementById('close-edit-popup');
const legendTabBtn = document.getElementById('legend-tab-btn');
const layerInfoModal = document.getElementById('layer-info-modal');
const emptyMapOverlay = document.getElementById('empty-map-overlay');
const emptyMapBrowseBtn = document.getElementById('empty-map-browse-btn');
const browseDropdownMenu = document.getElementById('browse-dropdown-menu');
const createLayerOption = document.getElementById('create-layer-option');
const aiGenerateOption = document.getElementById('ai-generate-option');
const browseFilesOption = document.getElementById('browse-files-option');
const loadFromMemoryOption = document.getElementById('load-from-memory-option');
const memoryModal = document.getElementById('memory-modal');
const memoryList = document.getElementById('memory-list');
const closeMemoryModalBtn = document.getElementById('close-memory-modal-btn');
const clearMemoryBtn = document.getElementById('clear-memory-btn');
const modalLayerName = document.getElementById('modal-layer-name');
const modalLayerInfo = document.getElementById('modal-layer-info');
const closeModalBtn = document.getElementById('close-modal-btn');

// Auth UI Elements
const userMenuBtn = document.getElementById('user-menu-btn');
const userDropdown = document.getElementById('user-dropdown');
const userIconLoggedOut = document.getElementById('user-icon-logged-out');
const userAvatar = document.getElementById('user-avatar');
const signInBtn = document.getElementById('sign-in-btn');
const signUpBtn = document.getElementById('sign-up-btn');
const signOutBtn = document.getElementById('sign-out-btn');
const userProfileBtn = document.getElementById('user-profile-btn');
const userDropdownAnonymous = document.getElementById('user-dropdown-anonymous');
const userDropdownAuthenticated = document.getElementById('user-dropdown-authenticated');
const userName = document.getElementById('user-name');
const userEmail = document.getElementById('user-email');

// Settings Modal Elements
const settingsBtn = document.getElementById('settings-btn');
const settingsModal = document.getElementById('settings-modal');
const closeSettingsBtn = document.getElementById('close-settings-btn');
const aiProviderSelect = document.getElementById('ai-provider-select');
const loadModelsBtn = document.getElementById('load-models-btn');
const saveSettingsBtn = document.getElementById('save-settings-btn');
const apiTestStatus = document.getElementById('api-test-status');
const modelSelectionGroup = document.getElementById('model-selection-group');
const aiModelSelect = document.getElementById('ai-model-select');
const modelHelpText = document.getElementById('model-help-text');

// AI Generation Modal Elements
const aiGenerateModal = document.getElementById('ai-generate-modal');
const closeAiGenerateBtn = document.getElementById('close-ai-generate-btn');
const aiPromptInput = document.getElementById('ai-prompt-input');
const confirmAiGenerateBtn = document.getElementById('confirm-ai-generate-btn');
const cancelAiGenerateBtn = document.getElementById('cancel-ai-generate-btn');
const aiGenerationStatus = document.getElementById('ai-generation-status');
const aiStatusText = document.getElementById('ai-status-text');

// Track if we've shown the "Changes Saved Locally" alert this session
let hasShownSaveAlert = false;

// Preview mode state
let previewState = {
    isActive: false,
    layerId: null,
    currentIndex: 0,
    intervalId: null,
    isPaused: false,
    delay: 3500 // 3.5 seconds between features (allows 2.5s zoom + 1s viewing time)
};


function showConfirm(title, message) {
    return new Promise((resolve) => {
        const modal = document.getElementById('confirm-modal');
        const titleElement = document.getElementById('confirm-modal-title');
        const messageElement = document.getElementById('confirm-modal-message');
        const okButton = document.getElementById('confirm-modal-ok');
        const cancelButton = document.getElementById('confirm-modal-cancel');
        const overlay = modal.querySelector('.modal-overlay');

        // Set content
        titleElement.textContent = title;
        messageElement.textContent = message;

        // Show modal
        modal.style.display = 'flex';

        // Handle OK
        const handleOk = () => {
            modal.style.display = 'none';
            cleanup();
            resolve(true);
        };

        // Handle Cancel
        const handleCancel = () => {
            modal.style.display = 'none';
            cleanup();
            resolve(false);
        };

        // Cleanup listeners
        const cleanup = () => {
            okButton.removeEventListener('click', handleOk);
            cancelButton.removeEventListener('click', handleCancel);
            overlay.removeEventListener('click', handleCancel);
            document.removeEventListener('keydown', handleEscape);
        };

        // Handle Escape key
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                handleCancel();
            }
        };

        // Add listeners
        okButton.addEventListener('click', handleOk);
        cancelButton.addEventListener('click', handleCancel);
        overlay.addEventListener('click', handleCancel);
        document.addEventListener('keydown', handleEscape);
    });
}

function showAlert(title, message) {
    return new Promise((resolve) => {
        const modal = document.getElementById('alert-modal');
        const titleElement = document.getElementById('alert-modal-title');
        const messageElement = document.getElementById('alert-modal-message');
        const okButton = document.getElementById('alert-modal-ok');
        const overlay = modal.querySelector('.modal-overlay');

        // Set content
        titleElement.textContent = title;
        messageElement.textContent = message;

        // Show modal
        modal.style.display = 'flex';

        // Handle OK
        const handleOk = () => {
            modal.style.display = 'none';
            cleanup();
            resolve();
        };

        // Cleanup listeners
        const cleanup = () => {
            okButton.removeEventListener('click', handleOk);
            overlay.removeEventListener('click', handleOk);
            document.removeEventListener('keydown', handleEnter);
        };

        // Handle Enter or Escape key
        const handleEnter = (e) => {
            if (e.key === 'Enter' || e.key === 'Escape') {
                handleOk();
            }
        };

        // Add listeners
        okButton.addEventListener('click', handleOk);
        overlay.addEventListener('click', handleOk);
        document.addEventListener('keydown', handleEnter);

        // Focus the OK button
        setTimeout(() => okButton.focus(), 100);
    });
}

/**
 * Show a prompt dialog for user input
 * @param {string} message - The prompt message
 * @param {string} defaultValue - Default input value
 * @returns {Promise<string|null>} The user input or null if cancelled
 */
function showPrompt(message, defaultValue = '') {
    return new Promise((resolve) => {
        const modal = document.createElement('div');
        modal.className = 'modal-overlay';
        modal.style.display = 'flex';

        modal.innerHTML = `
            <div class="modal-content" style="max-width: 400px;">
                <div class="modal-header">
                    <h3>${message}</h3>
                </div>
                <div class="modal-body">
                    <input type="text" id="prompt-input" class="form-input" value="${defaultValue}"
                           style="width: 100%; padding: 10px; border: 1px solid var(--color-border);
                                  border-radius: var(--radius-md); background: var(--color-bg-primary);
                                  color: var(--color-text-primary); font-size: 14px;" />
                </div>
                <div class="modal-footer">
                    <button id="prompt-cancel-btn" class="btn btn-secondary">Cancel</button>
                    <button id="prompt-ok-btn" class="btn btn-primary">OK</button>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        const input = modal.querySelector('#prompt-input');
        const okBtn = modal.querySelector('#prompt-ok-btn');
        const cancelBtn = modal.querySelector('#prompt-cancel-btn');

        // Focus and select the input
        setTimeout(() => {
            input.focus();
            input.select();
        }, 100);

        const handleOk = () => {
            const value = input.value.trim();
            document.body.removeChild(modal);
            resolve(value || null);
        };

        const handleCancel = () => {
            document.body.removeChild(modal);
            resolve(null);
        };

        okBtn.addEventListener('click', handleOk);
        cancelBtn.addEventListener('click', handleCancel);

        // Handle Enter key
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                handleOk();
            } else if (e.key === 'Escape') {
                handleCancel();
            }
        });

        // Click outside to close
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                handleCancel();
            }
        });
    });
}

// Make modal functions globally available
window.showConfirm = showConfirm;
window.showPrompt = showPrompt;
window.showAlert = showAlert;

// Make database reset available for debugging (console: await window.resetDatabase())
window.resetDatabase = resetDatabase;

function showProjectionModal(layer) {
    return new Promise((resolve) => {
        const modal = document.getElementById('projection-modal');
        const exportBtn = document.getElementById('projection-export-btn');
        const cancelBtn = document.getElementById('projection-cancel-btn');
        const closeBtn = document.getElementById('close-projection-modal-btn');
        const overlay = modal.querySelector('.modal-overlay');
        const originalOption = document.getElementById('original-projection-option');
        const originalDesc = document.getElementById('original-projection-desc');

        // Check if layer has original projection info
        let hasOriginalProjection = false;
        if (layer.metadata && layer.metadata.prj) {
            hasOriginalProjection = true;
            // Show original projection option
            originalOption.style.display = 'flex';

            // Use the pre-extracted projection name from metadata
            let projectionName = layer.metadata.originalProjection;
            const prjWKT = layer.metadata.prj;

            // If originalProjection is missing, try to extract it from WKT
            if (!projectionName) {
                let nameMatch = prjWKT.match(/PROJCS\["([^"]+)"/);
                if (nameMatch) {
                    projectionName = nameMatch[1];
                } else {
                    nameMatch = prjWKT.match(/GEOGCS\["([^"]+)"/);
                    projectionName = nameMatch ? nameMatch[1] : 'Custom Projection';
                }
            }

            // Try to extract EPSG code from WKT
            let epsgCode = null;
            const epsgMatch = prjWKT.match(/AUTHORITY\["EPSG","(\d+)"\]/);
            if (epsgMatch) {
                epsgCode = `EPSG:${epsgMatch[1]}`;
            }

            // Build description with EPSG code if available
            let description = '';
            if (epsgCode) {
                description = `${epsgCode} • ${projectionName}`;
            } else {
                description = projectionName;
            }

            // Show a preview of the WKT (first 80 characters)
            const wktPreview = prjWKT.substring(0, 80) + (prjWKT.length > 80 ? '...' : '');

            originalDesc.innerHTML = `
                <div style="line-height: 1.4;">
                    <div style="font-weight: 600; margin-bottom: 2px;">${description}</div>
                    <div style="font-size: 11px; color: var(--color-text-muted); font-family: 'Courier New', monospace; margin-top: 4px;">${wktPreview}</div>
                </div>
            `;

            // Store the WKT for later use
            originalOption.dataset.prj = prjWKT;
            originalOption.dataset.prjName = projectionName;
        } else {
            // Hide original projection option
            originalOption.style.display = 'none';
        }

        // Default to original projection if available, otherwise WGS84
        if (hasOriginalProjection) {
            const originalRadio = modal.querySelector('input[value="original"]');
            if (originalRadio) originalRadio.checked = true;
        } else {
            const wgs84Radio = modal.querySelector('input[value="EPSG:4326"]');
            if (wgs84Radio) wgs84Radio.checked = true;
        }

        // Show modal
        modal.style.display = 'flex';

        // Handle Export
        const handleExport = () => {
            const selectedRadio = modal.querySelector('input[name="projection"]:checked');
            if (!selectedRadio) {
                resolve(null);
                cleanup();
                return;
            }

            const value = selectedRadio.value;
            let projection, projectionName;

            if (value === 'EPSG:4326') {
                projection = 'EPSG:4326';
                projectionName = 'WGS84';
            } else if (value === 'EPSG:3857') {
                projection = 'EPSG:3857';
                projectionName = 'Web Mercator';
            } else if (value === 'original') {
                projection = originalOption.dataset.prj;
                projectionName = originalOption.dataset.prjName;
            }

            modal.style.display = 'none';
            cleanup();
            resolve({ projection, projectionName });
        };

        // Handle Cancel
        const handleCancel = () => {
            modal.style.display = 'none';
            cleanup();
            resolve(null);
        };

        // Cleanup listeners
        const cleanup = () => {
            exportBtn.removeEventListener('click', handleExport);
            cancelBtn.removeEventListener('click', handleCancel);
            closeBtn.removeEventListener('click', handleCancel);
            overlay.removeEventListener('click', handleCancel);
            document.removeEventListener('keydown', handleEscape);
        };

        // Handle Escape key
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                handleCancel();
            }
        };

        // Add listeners
        exportBtn.addEventListener('click', handleExport);
        cancelBtn.addEventListener('click', handleCancel);
        closeBtn.addEventListener('click', handleCancel);
        overlay.addEventListener('click', handleCancel);
        document.addEventListener('keydown', handleEscape);
    });
}

function initWelcomeModal() {
    const welcomeModal = document.getElementById('welcome-modal');
    const welcomeCloseBtn = document.getElementById('welcome-close-btn');
    const welcomeTryBtn = document.getElementById('welcome-try-btn');
    const welcomeLearnBtn = document.getElementById('welcome-learn-btn');
    const welcomeGetStartedBtn = document.getElementById('welcome-get-started-btn');
    const welcomeBackBtn = document.getElementById('welcome-back-btn');
    const mainView = document.getElementById('welcome-main-view');
    const learnView = document.getElementById('welcome-learn-view');

    // Always show modal on app load
    welcomeModal.style.display = 'flex';

    // Helper function to switch views
    function showMainView() {
        mainView.style.display = 'flex';
        learnView.style.display = 'none';
    }

    function showLearnView() {
        mainView.style.display = 'none';
        learnView.style.display = 'flex';
    }

    // Close button
    welcomeCloseBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        welcomeModal.style.display = 'none';
    });

    // Try it Now button - close the modal
    welcomeTryBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        welcomeModal.style.display = 'none';
        // Tutorial disabled for now - you can manually trigger with startTutorial('getting-started')
    });

    // Learn More button - show learn more view
    welcomeLearnBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        showLearnView();
    });

    // Get Started button - close the modal
    welcomeGetStartedBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        welcomeModal.style.display = 'none';
    });

    // Back button - return to main view
    welcomeBackBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        showMainView();
    });

    // Prevent any auto-close or accidental dismissal
    welcomeModal.addEventListener('click', (e) => {
        e.stopPropagation();
    });
}

// ===== AUTHENTICATION FUNCTIONS =====

/**
 * Initialize Clerk authentication
 */
async function initAuth() {
    try {
        const clerk = await initializeClerk();

        if (!clerk) {
            console.log('✓ Anonymous mode active');
        } else {
            console.log('✓ Clerk authentication ready');
            // Listen for auth state changes
            onAuthStateChange((user) => {
                console.log('Auth state changed:', user ? `User ${user.id}` : 'Anonymous');
                updateAuthUI();
                handleUserChange(user);
            });
        }

        updateAuthUI();
    } catch (error) {
        console.error('✗ Auth initialization failed:', error.message);
        // Continue in anonymous mode
        updateAuthUI();
    }
}

/**
 * Update authentication UI based on current user
 */
function updateAuthUI() {
    const clerk = getClerk();
    const user = getCurrentUser();

    // If Clerk is not initialized (localhost without test keys), hide the user menu
    if (!clerk) {
        if (userMenuBtn) userMenuBtn.style.display = 'none';
        console.log('Auth UI hidden (Clerk disabled on localhost)');
        return;
    }

    // Show user menu
    if (userMenuBtn) userMenuBtn.style.display = 'block';

    if (user) {
        // User is authenticated
        userIconLoggedOut.style.display = 'none';
        userAvatar.style.display = 'block';
        userAvatar.src = user.imageUrl || '';
        userAvatar.alt = user.fullName || 'User';

        // Show authenticated dropdown
        userDropdownAnonymous.style.display = 'none';
        userDropdownAuthenticated.style.display = 'block';

        // Update user info
        userName.textContent = user.fullName || 'User';
        userEmail.textContent = user.primaryEmailAddress?.emailAddress || '';
    } else {
        // User is anonymous
        userIconLoggedOut.style.display = 'block';
        userAvatar.style.display = 'none';

        // Show anonymous dropdown
        userDropdownAnonymous.style.display = 'block';
        userDropdownAuthenticated.style.display = 'none';
    }
}

/**
 * Handle user change (sign in/out)
 * Reload layers for the new user
 */
async function handleUserChange(user) {
    try {
        // Clear current layers from map
        const layers = getAllLayers();
        layers.forEach(layer => {
            removeLayerFromMap(layer.id);
        });

        // Clear layer manager
        clearAllLayers();

        // Update legend
        updateLegend();

        // Reload layers for new user (will be empty if new user or anonymous)
        // This is handled automatically when user opens memory modal or loads files

        console.log('Switched to user:', user ? user.id : 'anonymous');
    } catch (error) {
        console.error('Error handling user change:', error);
    }
}

async function init() {
    try {
        // Initialize Vercel Analytics
        inject();

        // Initialize database
        await initDB();
        console.log('Database initialized');

        // Initialize authentication
        await initAuth();
        console.log('Authentication initialized');

        // Initialize map
        initMap('map');
        console.log('Map initialized');

        // Set dark mode toggle to active (map starts in dark mode)
        darkMapToggle.classList.add('active');
        darkMapToggle.title = 'Switch to Light Map';

        // Add keyboard listener for ESC to exit drawing mode
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && isEditMode()) {
                stopDrawingMode();
                // Reset draw button style
                const legendDrawBtn = document.getElementById('legend-draw-btn');
                if (legendDrawBtn) {
                    legendDrawBtn.style.background = '';
                    legendDrawBtn.style.borderColor = '';
                    legendDrawBtn.style.color = '';
                }
            }
        });

        // Initialize modules
        const tableScrollContainer = document.querySelector('.table-scroll');
        initializeLayerManager(tableScrollContainer);
        initializeTabManager(
            (layerId) => {
                // Called when switching tabs
                setCurrentLayer(layerId, (index) => {
                    // Highlight row and zoom to feature (don't scroll table)
                    highlightTableRow(index, true, false);
                });
            },
            (layerId) => {
                // Called when removing a tab
                removeLayer(layerId);
                updateLegend();

                // Legend tab is always visible now
                // if (getLayerCount() === 0) {
                //     legendTabBtn.style.display = 'none';
                //     switchTab('upload-tab');
                // }
            }
        );
        console.log('Modules initialized');

        // Initialize tutorial system
        initializeTutorial();
        console.log('Tutorial system initialized');

        // Setup event listeners
        setupEventListeners();

        // Load history
        await loadHistory();

        // Show welcome modal on first load
        initWelcomeModal();

        showStatus('Ready! Drop files, folders, or browse to add layers', 'info');
    } catch (error) {
        console.error('Initialization error:', error);
        showStatus(`Error initializing app: ${error.message}`, 'error');
    }
}


function setupEventListeners() {
    // Browse button - toggle dropdown menu
    browseBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        toggleBrowseDropdown();
    });

    // Create Layer option
    createLayerOption.addEventListener('click', async () => {
        closeBrowseDropdown();
        await createNewLayer();
    });

    // Browse Files option
    browseFilesOption.addEventListener('click', () => {
        fileInput.click();
        closeBrowseDropdown();
    });

    // Load from Memory option
    loadFromMemoryOption.addEventListener('click', async () => {
        closeBrowseDropdown();
        await showMemoryModal();
    });

    // AI Generate option
    aiGenerateOption.addEventListener('click', async () => {
        closeBrowseDropdown();
        await showAiGenerateModal();
    });

    // Empty map browse button
    emptyMapBrowseBtn.addEventListener('click', () => {
        fileInput.click();
    });

    // Empty map close button
    const emptyMapCloseBtn = document.getElementById('empty-map-close-btn');
    emptyMapCloseBtn.addEventListener('click', () => {
        emptyMapOverlay.classList.add('hidden');
    });

    // Close memory modal
    closeMemoryModalBtn.addEventListener('click', () => {
        closeMemoryModal();
    });

    // Clear all memory
    clearMemoryBtn.addEventListener('click', async () => {
        const confirmed = await showConfirm(
            'Clear Memory',
            'Are you sure you want to clear all files from memory? This action cannot be undone.'
        );
        if (confirmed) {
            await clearHistory();
            closeMemoryModal();
        }
    });

    // Close memory modal when clicking overlay
    memoryModal.querySelector('.modal-overlay').addEventListener('click', () => {
        closeMemoryModal();
    });

    // ===== AUTH EVENT LISTENERS =====

    // User menu toggle
    userMenuBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        const isVisible = userDropdown.style.display === 'block';
        userDropdown.style.display = isVisible ? 'none' : 'block';
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
        if (userDropdown && !userMenuBtn.contains(e.target) && !userDropdown.contains(e.target)) {
            userDropdown.style.display = 'none';
        }
    });

    // Sign in button
    signInBtn?.addEventListener('click', async () => {
        userDropdown.style.display = 'none';
        try {
            await openSignIn();
        } catch (error) {
            console.error('Error opening sign in:', error);
        }
    });

    // Sign up button
    signUpBtn?.addEventListener('click', async () => {
        userDropdown.style.display = 'none';
        try {
            await openSignUp();
        } catch (error) {
            console.error('Error opening sign up:', error);
        }
    });

    // User profile button
    userProfileBtn?.addEventListener('click', async () => {
        userDropdown.style.display = 'none';
        try {
            await openUserProfile();
        } catch (error) {
            console.error('Error opening user profile:', error);
        }
    });

    // Sign out button
    signOutBtn?.addEventListener('click', async () => {
        userDropdown.style.display = 'none';
        try {
            await signOut();
        } catch (error) {
            console.error('Error signing out:', error);
        }
    });

    // ===== SETTINGS MODAL =====

    // Settings button
    settingsBtn.addEventListener('click', () => {
        showSettingsModal();
    });

    // Close settings modal
    closeSettingsBtn.addEventListener('click', () => {
        closeSettingsModal();
    });

    // Settings modal overlay click
    settingsModal.querySelector('.modal-overlay').addEventListener('click', () => {
        closeSettingsModal();
    });

    // AI Provider selection
    aiProviderSelect.addEventListener('change', () => {
        const provider = aiProviderSelect.value;
        AI_SERVICE.setProvider(provider);

        // Reset model selection
        modelSelectionGroup.style.display = 'none';
        apiTestStatus.style.display = 'none';
        aiModelSelect.innerHTML = '<option value="">Select a model...</option>';
    });

    // Load available models
    loadModelsBtn?.addEventListener('click', async () => {
        const provider = aiProviderSelect.value;

        loadModelsBtn.disabled = true;
        loadModelsBtn.textContent = 'Loading...';
        apiTestStatus.style.display = 'block';
        apiTestStatus.innerHTML = '<p style="color: #667eea;">Loading available models from server...</p>';

        try {
            const response = await fetch('http://localhost:3001/api/test-models', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ provider })
            });

            const data = await response.json();

            if (data.valid && data.availableModels.length > 0) {
                apiTestStatus.innerHTML = `
                    <p style="color: #43e97b; font-weight: 500;">✅ Models Loaded!</p>
                    <p style="color: #666; font-size: 13px; margin-top: 4px;">
                        Found ${data.availableModels.length} available model(s)
                    </p>
                `;

                // Populate model dropdown
                aiModelSelect.innerHTML = '<option value="">Select a model...</option>';
                data.availableModels.forEach(model => {
                    const option = document.createElement('option');
                    option.value = model.model;
                    option.textContent = model.displayName;
                    aiModelSelect.appendChild(option);
                });

                // Show model selection and set first model as default
                modelSelectionGroup.style.display = 'block';
                aiModelSelect.value = data.availableModels[0].model;

                // Update help text
                modelHelpText.textContent = 'Select the model you want to use for AI generation.';
            } else {
                apiTestStatus.innerHTML = `
                    <p style="color: #fa709a; font-weight: 500;">❌ No Models Available</p>
                    <p style="color: #666; font-size: 13px; margin-top: 4px;">
                        ${data.error || 'API key not configured on server.'}
                    </p>
                `;
                modelSelectionGroup.style.display = 'none';
            }
        } catch (error) {
            console.error('Load models error:', error);
            apiTestStatus.innerHTML = `
                <p style="color: #fa709a; font-weight: 500;">❌ Load Failed</p>
                <p style="color: #666; font-size: 13px; margin-top: 4px;">
                    ${error.message || 'Could not connect to the server'}
                </p>
            `;
            modelSelectionGroup.style.display = 'none';
        } finally {
            loadModelsBtn.disabled = false;
            loadModelsBtn.textContent = 'Load Available Models';
        }
    });

    // Save settings
    saveSettingsBtn.addEventListener('click', async () => {
        const provider = aiProviderSelect.value;
        const selectedModel = aiModelSelect.value;

        if (!selectedModel) {
            await showAlert('Model Required', 'Please load models and select one first.');
            return;
        }

        AI_SERVICE.setProvider(provider);
        AI_SERVICE.setModel(selectedModel);
        closeSettingsModal();
        await showAlert('Settings Saved', 'Your AI provider and model preferences have been saved.');
    });

    // AI Generate modal close button
    closeAiGenerateBtn.addEventListener('click', () => {
        closeAiGenerateModal();
    });

    // AI Generate modal overlay click
    aiGenerateModal.querySelector('.modal-overlay').addEventListener('click', () => {
        closeAiGenerateModal();
    });

    // Cancel AI generate
    cancelAiGenerateBtn.addEventListener('click', () => {
        closeAiGenerateModal();
    });

    // Confirm AI generate
    confirmAiGenerateBtn.addEventListener('click', async () => {
        await handleAiGenerate();
    });

    // Submit on Enter (Ctrl+Enter for textarea)
    aiPromptInput.addEventListener('keydown', async (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault();
            await handleAiGenerate();
        }
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
        if (!browseDropdownMenu.contains(e.target) && !browseBtn.contains(e.target)) {
            closeBrowseDropdown();
        }
    });

    // File input change
    fileInput.addEventListener('change', async (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            // If single file or multiple files
            await handleFileUpload(files.length === 1 ? files[0] : files);
        }
    });

    // Drag and drop on map
    setupDragAndDrop(mapContainer, handleFileUpload);

    // Sample data button
    const sampleDataBtn = document.getElementById('sample-data-btn');
    if (sampleDataBtn) {
        sampleDataBtn.addEventListener('click', async (e) => {
            e.preventDefault();

            try {
                showStatus('Loading sample data...', 'info');
                showProgress(10, 'Preparing data...');

                // Load US States polygons from local sample data
                showProgress(30, 'Fetching US States data...');
                const statesResponse = await fetch('/sample-data/us-states.geojson');
                if (!statesResponse.ok) throw new Error('Failed to load US States sample data');
                const statesGeoJSON = await statesResponse.json();

                // Load states layer (skip save and skip fitBounds)
                showProgress(60, 'Loading US States layer...');
                const statesBlob = new Blob([JSON.stringify(statesGeoJSON)], { type: 'application/json' });
                const statesFile = new File([statesBlob], 'us_states.geojson', { type: 'application/json' });
                await handleFileUpload(statesFile, true, true);

                showProgress(100, 'Complete!');
                showStatus('Sample data loaded successfully!', 'success');

                // Custom zoom to show all US states - wait for map to be idle after loading
                const map = getMap();
                if (map) {
                    map.once('idle', () => {
                        map.flyTo({
                            center: [-98.5, 38.5], // Kansas center coordinates
                            zoom: 2, // Wider zoom to see all contiguous US states
                            duration: 1000
                        });
                    });
                }

            } catch (error) {
                console.error('Error loading sample data:', error);
                showStatus(`Error loading sample data: ${error.message}`, 'error');
                hideProgress();
            }
        });
    }

    // Show table button
    showTableBtn.addEventListener('click', () => {
        showTable();
    });

    // Close attribute table
    closeTableBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        hideTable(); // Use hideTable() to properly close table, tabs, and scroll
        removeHighlight();

        // Disable edit mode if active
        if (isEditMode()) {
            toggleEditMode(false);
            updateLegend(); // Update legend to hide edit controls
        }
    });

    // Dark map toggle
    darkMapToggle.addEventListener('click', () => {
        const isDark = toggleDarkMap();
        if (isDark) {
            darkMapToggle.classList.add('active');
            darkMapToggle.title = 'Switch to Light Map';
        } else {
            darkMapToggle.classList.remove('active');
            darkMapToggle.title = 'Toggle Dark Map';
        }
    });

    // Preview play button
    previewPlayBtn.addEventListener('click', () => {
        if (previewState.isActive) {
            togglePreviewPlayPause();
        }
    });

    // Preview back button
    previewBackBtn.addEventListener('click', () => {
        if (previewState.isActive) {
            previewPreviousFeature();
        }
    });

    // Preview forward button
    previewForwardBtn.addEventListener('click', () => {
        if (previewState.isActive) {
            previewNextFeature();
        }
    });

    // Preview close button
    previewCloseBtn.addEventListener('click', () => {
        stopPreviewMode();
    });

    // Edit Controls are now in the legend, no need for popup handlers
    // Edit mode is now controlled from the legend layer actions

    // Listen for edit history changes to update button states
    document.addEventListener('editHistoryChanged', (e) => {
        // Update the edit controls (both popup and legend buttons)
        updateEditControlButtons();
    });

    // Keyboard shortcuts for undo/redo
    document.addEventListener('keydown', (e) => {
        // Only handle shortcuts when in edit mode
        if (!isEditMode()) return;

        // Ctrl+Z or Cmd+Z for undo
        if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
            e.preventDefault();
            if (undoEdit()) {
                showStatus('Undo', 'info');
            }
        }

        // Ctrl+Y or Cmd+Y or Ctrl+Shift+Z for redo
        if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
            e.preventDefault();
            if (redoEdit()) {
                showStatus('Redo', 'info');
            }
        }
    });

    document.addEventListener('layerEdited', () => {
        updateLegend();
        // Re-sync draw button state after legend recreates the button
        updateDrawButtonState(isDrawingMode());
    });

    document.addEventListener('updateLegend', () => {
        updateLegend();
        // Re-sync draw button state after legend recreates the button
        updateDrawButtonState(isDrawingMode());
    });

    document.addEventListener('showStatus', (event) => {
        const { message, type } = event.detail;
        showStatus(message, type);
    });

    // Tab navigation
    document.querySelectorAll('.tab-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const tabId = btn.dataset.tab;
            switchTab(tabId);
        });
    });

    // Modal close handlers
    closeModalBtn.addEventListener('click', () => {
        layerInfoModal.style.display = 'none';
    });

    layerInfoModal.querySelector('.modal-overlay').addEventListener('click', () => {
        layerInfoModal.style.display = 'none';
    });

    // Close modal on Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && layerInfoModal.style.display !== 'none') {
            layerInfoModal.style.display = 'none';
        }
    });

    // Table resize handle
    const tableResizeHandle = document.getElementById('table-resize-handle');
    let isResizing = false;
    let startY = 0;
    let startHeight = 0;

    tableResizeHandle.addEventListener('mousedown', (e) => {
        isResizing = true;
        startY = e.clientY;
        startHeight = attributeTableContainer.offsetHeight;
        tableResizeHandle.classList.add('dragging');
        document.body.style.cursor = 'ns-resize';
        document.body.style.userSelect = 'none';
        e.preventDefault();
    });

    document.addEventListener('mousemove', (e) => {
        if (!isResizing) return;

        const deltaY = startY - e.clientY;
        const newHeight = startHeight + deltaY;
        const minHeight = 150; // Minimum table height
        const maxHeight = window.innerHeight * 0.8; // Maximum 80% of viewport

        if (newHeight >= minHeight && newHeight <= maxHeight) {
            // Set table height
            attributeTableContainer.style.height = `${newHeight}px`;

            // Adjust map height to fill remaining space
            const mapElement = document.getElementById('map');
            const remainingHeight = window.innerHeight - newHeight;
            if (mapElement) {
                mapElement.style.height = `${remainingHeight}px`;
            }

            // Trigger map resize
            resizeMap();
        }
    });

    document.addEventListener('mouseup', () => {
        if (isResizing) {
            isResizing = false;
            tableResizeHandle.classList.remove('dragging');
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }
    });
}

function switchTab(tabId) {
    // Remove active class from all tabs and content
    document.querySelectorAll('.tab-btn').forEach(btn => btn.classList.remove('active'));
    document.querySelectorAll('.tab-content').forEach(content => content.classList.remove('active'));

    // Add active class to selected tab and content
    const selectedBtn = document.querySelector(`[data-tab="${tabId}"]`);
    const selectedContent = document.getElementById(tabId);

    if (selectedBtn) selectedBtn.classList.add('active');
    if (selectedContent) selectedContent.classList.add('active');
}

async function handleFileUpload(file, skipSave = false, skipFitBounds = false) {
    try {
        let result;
        let metadata;

        let files = Array.isArray(file) ? file : [file];
        const isSingleFile = !Array.isArray(file);

        const hasShapefileComponent = files.some(f =>
            /\.(shp|dbf|shx|prj|cpg)$/i.test(f.name)
        );

        if (files.length > 1 || hasShapefileComponent) {
            console.log('Multiple files detected:', files.length, 'Has shapefile component:', hasShapefileComponent);

            try {
                console.log('Checking for shapefile components in:', files.map(f => f.name));
                const shapefileData = detectShapefileComponents(files);
                console.log('Detection result:', shapefileData);

                if (shapefileData) {
                    console.log('Detected shapefile components:', shapefileData);
                    showStatus('Detected shapefile components, processing...', 'info');
                    showProgress(10, 'Combining shapefile components...');

                    const virtualZip = await createVirtualZip(shapefileData);

                    showProgress(30, 'Loading shapefile...');

                    result = await loadShapefile(virtualZip, (progress, message) => {
                        showProgress(30 + (progress * 0.7), message);
                    });

                    showProgress(100, 'Processing complete!');
                    metadata = extractMetadata(result.geojson);

                    console.log(`Loaded shapefile from ${Object.keys(shapefileData.components).filter(k => shapefileData.components[k]).length} component files`);

                    // Save to IndexedDB FIRST to get dbId (skip for sample data)
                    let dbId = null;
                    if (!skipSave) {
                        dbId = await saveShapefile({
                            name: result.fileName,
                            geojson: result.geojson,
                            metadata: metadata
                        }, getUserId());
                    }

                    // Add layer to map with dbId
                    const layerId = addLayer({
                        name: result.fileName,
                        geojson: result.geojson,
                        metadata: metadata,
                        featureCount: result.featureCount,
                        dbId: dbId // Pass dbId so edits can be saved
                    }, (index) => {
                        highlightTableRow(index, false, true);
                    }, { skipFitBounds });

                    // Zoom to layer bounds
                    if (metadata && metadata.bounds && !skipFitBounds) {
                        const bounds = metadata.bounds;
                        const map = getMap();
                        if (map) {
                            map.fitBounds([
                                [bounds.minLon, bounds.minLat],
                                [bounds.maxLon, bounds.maxLat]
                            ], {
                                padding: 50,
                                duration: 1000,
                                maxZoom: 16
                            });
                        }
                    }

                    // Update UI
                    updateLegend();
                    if (!skipSave) {
                        await loadHistory();
                    }

                    showStatus(`Successfully loaded ${result.fileName} with ${result.featureCount} features`, 'success');
                    hideProgress();

                    // Reset file input
                    fileInput.value = '';
                    return;
                }
            } catch (shapefileError) {
                console.error('Shapefile detection/processing error:', shapefileError);

                if (shapefileError.message.includes('Missing required')) {
                    await window.showAlert(
                        'Missing Shapefile Components',
                        `${shapefileError.message}\n\nThe browser needs ALL files selected together (not just in the same folder).\n\nHow to select multiple files:\n\n1. Hold Ctrl (Cmd on Mac) and click each file\n2. Or drag-select all files together\n3. Or zip the folder and drop the .zip\n\nRequired:\n• .shp (geometry)\n• .dbf (attributes)\n• .shx (index)\n\nOptional:\n• .prj (projection)\n• .cpg (encoding)`
                    );
                    hideProgress();
                    fileInput.value = '';
                    return;
                }

                if (hasShapefileComponent && isSingleFile) {
                    await window.showAlert(
                        'Select All Shapefile Files',
                        'You dropped only the .shp file. The browser cannot access other files in the same folder automatically.\n\nPlease select/drop ALL files together:\n\n1. Hold Ctrl (or Cmd on Mac) and click all files:\n   • parcels.shp\n   • parcels.dbf\n   • parcels.shx\n   • parcels.prj (optional)\n\n2. Or drag all files together\n\n3. Or zip the folder and drop the .zip file'
                    );
                    hideProgress();
                    fileInput.value = '';
                    return;
                }
                // Otherwise, continue to check for other file types
            }

            // Check if all files are images
            const allImages = files.every(f => isImageFile(f));

            if (allImages && files.length > 1) {
                // Multiple photos - batch process
                showStatus(`Loading ${files.length} photos...`, 'info');
                showProgress(0, 'Processing photos...');

                const result = await loadPhotos(files, (progress, message) => {
                    showProgress(progress, message);
                });

                showProgress(100, 'Processing complete!');
                const metadata = extractMetadata(result.geojson);

                console.log(`Processed ${files.length} photos, ${result.featureCount} with GPS data`);

                // Show warning if some photos failed
                if (result.errors && result.errors.length > 0) {
                    showStatus(`Loaded ${result.featureCount} photos (${result.errors.length} skipped - no GPS data)`, 'info');
                }

                // Save to IndexedDB FIRST to get dbId (skip for sample data)
                let dbId = null;
                if (!skipSave) {
                    dbId = await saveShapefile({
                        name: result.fileName,
                        geojson: result.geojson,
                        metadata: metadata
                    }, getUserId());
                }

                // Add layer to map with dbId
                const layerId = addLayer({
                    name: result.fileName,
                    geojson: result.geojson,
                    metadata: metadata,
                    featureCount: result.featureCount,
                    dbId: dbId // Pass dbId so edits can be saved
                }, (index) => {
                    highlightTableRow(index, false);
                }, { skipFitBounds });

                // Zoom to layer bounds
                if (metadata && metadata.bounds && !skipFitBounds) {
                    const bounds = metadata.bounds;
                    const map = getMap();
                    if (map) {
                        map.fitBounds([
                            [bounds.minLon, bounds.minLat],
                            [bounds.maxLon, bounds.maxLat]
                        ], {
                            padding: 50,
                            duration: 1000,
                            maxZoom: 16
                        });
                    }
                }

                // Update UI
                updateLegend();
                if (!skipSave) {
                    await loadHistory();
                }

                showStatus(`Successfully loaded ${result.featureCount} photos`, 'success');
                hideProgress();

                // Reset file input
                fileInput.value = '';
                return;
            } else {
                // Multiple independent files (GeoJSON, KML, CSV, etc.)
                // Process each file sequentially
                console.log('Processing multiple independent files:', files.map(f => f.name));

                let successCount = 0;
                let failCount = 0;

                for (let i = 0; i < files.length; i++) {
                    const currentFile = files[i];
                    try {
                        console.log(`Processing file ${i + 1}/${files.length}: ${currentFile.name}`);
                        showStatus(`Loading ${i + 1}/${files.length}: ${currentFile.name}`, 'info');

                        // Process this single file recursively
                        await handleFileUpload(currentFile);
                        successCount++;
                    } catch (error) {
                        console.error(`Failed to process ${currentFile.name}:`, error);
                        failCount++;
                    }
                }

                // Show summary
                if (successCount > 0) {
                    showStatus(`Loaded ${successCount} layer${successCount !== 1 ? 's' : ''}${failCount > 0 ? `, ${failCount} failed` : ''}`, 'success');
                } else {
                    showStatus(`Failed to load files`, 'error');
                }

                hideProgress();
                fileInput.value = '';
                return;
            }
        }

        // Single file processing (original logic)
        const fileName = file.name.toLowerCase();

        // Check if it's an image file first
        if (isImageFile(file)) {
            // Load photo with GPS metadata
            showStatus('Reading photo GPS data...', 'info');
            showProgress(0, 'Reading file...');

            result = await loadPhoto(file, (progress, message) => {
                showProgress(progress, message);
            });

            showProgress(100, 'Processing complete!');
            metadata = extractMetadata(result.geojson);

            console.log(`Extracted GPS coordinates from photo: ${result.geojson.features[0].properties.latitude}, ${result.geojson.features[0].properties.longitude}`);

        } else if (fileName.endsWith('.zip')) {
            // Check if it's a shapefile or photo archive
            showStatus('Inspecting zip file...', 'info');
            showProgress(0, 'Reading file...');

            try {
                // Try to load as shapefile first
                result = await loadShapefile(file, (progress, message) => {
                    showProgress(progress, message);
                });

                showProgress(100, 'Processing complete!');
                metadata = extractMetadata(result.geojson);

            } catch (shapefileError) {
                // Not a valid shapefile - check if it contains photos
                console.log('Not a shapefile, checking for photos...', shapefileError);

                showStatus('Extracting photos from zip...', 'info');
                showProgress(10, 'Extracting photos...');

                try {
                    const photoFiles = await extractPhotosFromZip(file);

                    if (photoFiles.length === 0) {
                        throw new Error('No photos found in zip file. Make sure the zip contains JPG, PNG, or HEIC images.');
                    }

                    showProgress(20, `Found ${photoFiles.length} photos, processing...`);

                    // Process photos as batch
                    result = await loadPhotos(photoFiles, (progress, message) => {
                        // Adjust progress to account for extraction
                        showProgress(20 + (progress * 0.8), message);
                    });

                    showProgress(100, 'Processing complete!');
                    metadata = extractMetadata(result.geojson);

                    console.log(`Extracted and processed ${photoFiles.length} photos from zip`);

                    // Show warning if some photos failed
                    if (result.errors && result.errors.length > 0) {
                        showStatus(`Loaded ${result.featureCount} photos from zip (${result.errors.length} skipped - no GPS data)`, 'info');
                    }

                } catch (photoError) {
                    // Neither shapefile nor photo archive
                    throw new Error(`Invalid zip file: ${shapefileError.message}. Also checked for photos: ${photoError.message}`);
                }
            }

        } else if (fileName.endsWith('.csv') || fileName.endsWith('.txt') || fileName.endsWith('.racas')) {
            // Load CSV/TXT/RACAS
            const fileType = fileName.endsWith('.racas') ? 'RACAS' : (fileName.endsWith('.txt') ? 'TXT' : 'CSV');
            showStatus(`Loading ${fileType} file...`, 'info');
            showProgress(0, 'Reading file...');

            result = await loadCSV(file, (progress, message) => {
                showProgress(progress, message);
            });

            showProgress(100, 'Processing complete!');
            metadata = extractMetadata(result.geojson);

            // Show detected coordinate fields
            if (result.coordinateFields) {
                console.log(`Detected coordinates: ${result.coordinateFields.latField}, ${result.coordinateFields.lonField}`);
            }

        } else if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) {
            // Load Excel
            showStatus('Loading Excel file...', 'info');
            showProgress(0, 'Reading file...');

            result = await loadExcel(file, (progress, message) => {
                showProgress(progress, message);
            });

            showProgress(100, 'Processing complete!');
            metadata = extractMetadata(result.geojson);

            // Show detected coordinate fields
            if (result.coordinateFields) {
                console.log(`Detected coordinates: ${result.coordinateFields.latField}, ${result.coordinateFields.lonField}`);
            }

        } else if (fileName.endsWith('.json') || fileName.endsWith('.geojson')) {
            // Load GeoJSON
            showStatus('Loading GeoJSON file...', 'info');
            showProgress(0, 'Reading file...');

            result = await loadGeoJSON(file, (progress, message) => {
                showProgress(progress, message);
            });

            showProgress(100, 'Processing complete!');
            metadata = extractMetadata(result.geojson);

            // Show detected geometry types
            if (result.geometryTypes) {
                console.log(`Detected geometry types: ${result.geometryTypes.join(', ')}`);
            }

        } else if (fileName.endsWith('.kml')) {
            // Load KML
            showStatus('Loading KML file...', 'info');
            showProgress(0, 'Reading file...');

            result = await loadKML(file, (progress, message) => {
                showProgress(progress, message);
            });

            showProgress(100, 'Processing complete!');
            metadata = extractMetadata(result.geojson);

            // Show detected geometry types
            if (result.geometryTypes) {
                console.log(`Detected geometry types: ${result.geometryTypes.join(', ')}`);
            }

        } else if (fileName.endsWith('.gpx')) {
            // Load GPX
            showStatus('Loading GPX file...', 'info');
            showProgress(0, 'Reading file...');

            result = await loadGPX(file, (progress, message) => {
                showProgress(progress, message);
            });

            showProgress(100, 'Processing complete!');
            metadata = extractMetadata(result.geojson);

            // Show detected geometry types
            if (result.geometryTypes) {
                console.log(`Detected geometry types: ${result.geometryTypes.join(', ')}`);
            }

        } else {
            showStatus('Unsupported file format', 'error');
            return;
        }

        // Save to IndexedDB FIRST to get dbId (skip for sample data)
        let dbId = null;
        if (!skipSave) {
            dbId = await saveShapefile({
                name: result.fileName,
                geojson: result.geojson,
                metadata: metadata
            }, getUserId());
        }

        // Add layer to map and layer list with dbId
        const layerId = addLayer({
            name: result.fileName,
            geojson: result.geojson,
            metadata: metadata,
            featureCount: result.featureCount,
            dbId: dbId // Pass dbId so edits can be saved
        }, (index) => {
            // When feature clicked on map, highlight without zoom
            highlightTableRow(index, false);
        }, { skipFitBounds });

        // Zoom to layer bounds
        if (metadata && metadata.bounds && !skipFitBounds) {
            const bounds = metadata.bounds;
            const map = getMap();
            if (map) {
                map.fitBounds([
                    [bounds.minLon, bounds.minLat],
                    [bounds.maxLon, bounds.maxLat]
                ], {
                    padding: 50,
                    duration: 1000,
                    maxZoom: 16
                });
            }
        }

        // Update UI
        updateLegend();
        if (!skipSave) {
            await loadHistory();
        }

        showStatus(`Successfully loaded ${result.fileName} with ${result.featureCount} features`, 'success');
        hideProgress();

        // Reset file input
        fileInput.value = '';

    } catch (error) {
        console.error('Error loading file:', error);
        showStatus(`Error: ${error.message}`, 'error');
        hideProgress();
    }
}

let hasAutoSwitchedToLightMode = false;
function updateEmptyMapOverlay() {
    const layers = getAllLayers();
    if (layers.length === 0) {
        emptyMapOverlay.classList.remove('hidden');
        // Reset flag so it auto-switches again when layers are added
        hasAutoSwitchedToLightMode = false;

        // Close attribute table when all layers are removed
        attributeTableContainer.classList.remove('visible');
        mapContainer.classList.remove('table-visible');
        showTableBtn.style.display = 'none';

        // Switch to dark mode (starting state)
        const isDark = darkMapToggle.classList.contains('active');
        if (!isDark) {
            // Switch to dark mode
            toggleDarkMap();
            darkMapToggle.classList.add('active');
            darkMapToggle.title = 'Toggle Light Map';
        }
    } else {
        emptyMapOverlay.classList.add('hidden');

        // Auto-switch to light mode when first layer is added
        if (!hasAutoSwitchedToLightMode && layers.length > 0) {
            hasAutoSwitchedToLightMode = true;

            // Check if currently in dark mode
            const isDark = darkMapToggle.classList.contains('active');
            if (isDark) {
                // Switch to light mode
                toggleDarkMap();
                darkMapToggle.classList.remove('active');
                darkMapToggle.title = 'Toggle Dark Map';
            }
        }
    }
}

function updateLegend() {
    const layers = getAllLayers();

    // Update empty map overlay
    updateEmptyMapOverlay();

    if (layers.length === 0) {
        legendContent.innerHTML = '<p style="text-align: center; color: #7f8c8d; padding: 20px;">No layers loaded</p>';
        return;
    }

    const reversedLayers = [...layers].reverse();

    const html = reversedLayers.map(layer => {
        const { geometryTypes } = layer.metadata;
        const layerName = layer.name.replace(/\.zip$/i, '');

        // Determine primary geometry type
        const primaryGeometry = geometryTypes[0] || 'Unknown';
        let geometryIcon = 'polygon';
        let geometryLabel = primaryGeometry;

        if (primaryGeometry.includes('Point')) {
            geometryIcon = 'point';
            geometryLabel = 'Point Features';
        } else if (primaryGeometry.includes('Line')) {
            geometryIcon = 'line';
            geometryLabel = 'Line Features';
        } else if (primaryGeometry.includes('Polygon')) {
            geometryIcon = 'polygon';
            geometryLabel = 'Polygon Features';
        }

        return `
            <div class="legend-layer ${!layer.visible ? 'layer-hidden' : ''}" draggable="true" data-layer-id="${layer.id}">
                <div class="legend-layer-name">
                    <div style="display: flex; align-items: center; gap: 8px; flex: 1;">
                        <div class="legend-color-indicator" style="background: ${layer.color};"></div>
                        <span>${layerName}</span>
                    </div>
                    <div class="legend-layer-controls">
                        <button class="legend-control-btn legend-edit-toggle-btn"
                                data-layer-id="${layer.id}"
                                title="Edit layer">Edit</button>
                        <button class="legend-control-btn legend-visibility-btn"
                                data-layer-id="${layer.id}"
                                title="${layer.visible ? 'Hide layer' : 'Show layer'}">
                            ${layer.visible ? `
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                            ` : `
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                    <line x1="1" y1="1" x2="23" y2="23"></line>
                                </svg>
                            `}
                        </button>

                        <div class="legend-actions-dropdown">
                            <button class="legend-control-btn legend-actions-btn"
                                    data-layer-id="${layer.id}"
                                    title="Layer actions">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <circle cx="12" cy="12" r="1"></circle>
                                    <circle cx="12" cy="5" r="1"></circle>
                                    <circle cx="12" cy="19" r="1"></circle>
                                </svg>
                            </button>
                            <div class="legend-actions-menu" data-layer-id="${layer.id}">
                                <button class="legend-action-option legend-action-download" data-layer-id="${layer.id}">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                        <polyline points="7 10 12 15 17 10"></polyline>
                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                    </svg>
                                    Export
                                    <svg class="submenu-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <polyline points="9 18 15 12 9 6"></polyline>
                                    </svg>
                                </button>
                                <div class="legend-download-submenu" data-layer-id="${layer.id}">
                                    <button class="legend-download-option" data-format="shp">Shapefile (.zip)</button>
                                    <button class="legend-download-option" data-format="geojson">GeoJSON (.json)</button>
                                    <button class="legend-download-option" data-format="kml">KML (.kml)</button>
                                    <button class="legend-download-option" data-format="gpx">GPX (.gpx)</button>
                                    <button class="legend-download-option" data-format="csv">CSV (.csv)</button>
                                    <button class="legend-download-option" data-format="txt">TXT (.txt)</button>
                                    <button class="legend-download-option" data-format="xlsx">Excel (.xlsx)</button>
                                </div>
                                <button class="legend-action-option legend-action-table" data-layer-id="${layer.id}">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                        <line x1="3" y1="9" x2="21" y2="9"></line>
                                        <line x1="9" y1="21" x2="9" y2="9"></line>
                                    </svg>
                                    Open Table
                                </button>
                                <button class="legend-action-option legend-action-preview" data-layer-id="${layer.id}">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                        <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                    </svg>
                                    Preview Features
                                </button>
                                <button class="legend-action-option legend-action-info" data-layer-id="${layer.id}">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="12" y1="16" x2="12" y2="12"></line>
                                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                    </svg>
                                    Layer Info
                                </button>
                                <button class="legend-action-option legend-action-remove" data-layer-id="${layer.id}">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <polyline points="3 6 5 6 21 6"></polyline>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                    </svg>
                                    Remove Layer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="legend-symbol">
                    <div class="legend-symbol-icon ${geometryIcon}" style="background: ${geometryIcon === 'point' ? layer.color : geometryIcon === 'line' ? 'linear-gradient(180deg, transparent 40%, ' + layer.color + ' 40%, ' + layer.color + ' 60%, transparent 60%)' : 'rgba(' + hexToRgb(layer.color) + ', 0.3)'}; ${geometryIcon === 'polygon' ? 'border-color: ' + layer.color : ''}"></div>
                    <span class="legend-symbol-label">${geometryLabel}</span>
                    <span class="legend-feature-count">${layer.featureCount.toLocaleString()} features</span>
                </div>
            </div>
        `;
    }).join('');

    legendContent.innerHTML = html;

    // Add inline edit controls if in edit mode
    if (isEditMode()) {
        const editingLayerId = getCurrentEditLayerId();
        if (editingLayerId) {
            try {
                const editBtn = legendContent.querySelector(`.legend-edit-toggle-btn[data-layer-id="${editingLayerId}"]`);
                const legendLayer = legendContent.querySelector(`.legend-layer[data-layer-id="${editingLayerId}"]`);

                if (editBtn) {
                    editBtn.classList.add('editing-active');
                    editBtn.textContent = 'Save';
                }

                if (legendLayer) {
                    legendLayer.classList.add('editing-active');

                    // Add undo/redo buttons row between layer name and symbol
                    const symbolRow = legendLayer.querySelector('.legend-symbol');
                    if (symbolRow) {
                        const undoRedoHtml = `
                            <div class="legend-edit-controls-row">
                                <button class="legend-edit-inline-btn legend-inline-draw-btn" id="legend-draw-btn" title="Draw new feature">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <path d="M12 20h9"></path>
                                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                    </svg>
                                    Draw
                                </button>
                                <button class="legend-edit-inline-btn legend-inline-undo-btn" id="legend-undo-btn" title="Undo (Ctrl+Z)" disabled>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <path d="M3 7v6h6"></path>
                                        <path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"></path>
                                    </svg>
                                    Undo
                                </button>
                                <button class="legend-edit-inline-btn legend-inline-redo-btn" id="legend-redo-btn" title="Redo (Ctrl+Y)" disabled>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <path d="M21 7v6h-6"></path>
                                        <path d="M3 17a9 9 0 019-9 9 9 0 016 2.3l3 2.7"></path>
                                    </svg>
                                    Redo
                                </button>
                            </div>
                        `;

                        // Insert undo/redo row before the symbol row
                        symbolRow.insertAdjacentHTML('beforebegin', undoRedoHtml);
                    }
                }

                // Add event listeners for legend draw/undo/redo buttons
                const legendDrawBtn = document.getElementById('legend-draw-btn');
                const legendUndoBtn = document.getElementById('legend-undo-btn');
                const legendRedoBtn = document.getElementById('legend-redo-btn');

                if (legendDrawBtn) {
                    legendDrawBtn.addEventListener('click', (e) => {
                        e.stopPropagation();

                        // Check if currently in drawing mode
                        if (isDrawingMode()) {
                            // Exit drawing mode (button state updated in stopDrawingMode)
                            stopDrawingMode();
                        } else {
                            // Enter drawing mode (button state updated in startDrawingMode)
                            const layer = getLayer(editingLayerId);
                            if (layer && layer.metadata && layer.metadata.geometryType) {
                                const success = startDrawingMode(layer.metadata.geometryType);
                                if (success) {
                                    showStatus('Drawing mode active. Features auto-save.', 'info');
                                }
                            }
                        }
                    });
                }

                if (legendUndoBtn) {
                    legendUndoBtn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        undoEdit();
                        updateEditControlButtons();
                    });
                }

                if (legendRedoBtn) {
                    legendRedoBtn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        redoEdit();
                        updateEditControlButtons();
                    });
                }

                // Update button states
                updateEditControlButtons();
            } catch (error) {
                console.error('Error setting up edit controls:', error);
            }
        }
    }

    // Add event listeners

    // Click layer name to zoom to extent
    legendContent.querySelectorAll('.legend-layer').forEach(layerEl => {
        layerEl.addEventListener('click', (e) => {
            // Don't zoom if clicking on buttons or controls
            if (e.target.closest('button') || e.target.closest('.legend-layer-controls')) {
                return;
            }

            const layerId = layerEl.dataset.layerId;
            const layer = getLayer(layerId);
            if (layer && layer.metadata && layer.metadata.bounds) {
                const bounds = layer.metadata.bounds;
                const map = getMap();
                if (map) {
                    map.fitBounds([
                        [bounds.minLon, bounds.minLat],
                        [bounds.maxLon, bounds.maxLat]
                    ], {
                        padding: 50,
                        duration: 1000
                    });
                }
            }
        });
    });

    legendContent.querySelectorAll('.legend-visibility-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const layerId = btn.dataset.layerId;
            toggleLayerVisibility(layerId);
            updateLegend();
        });
    });

    // Actions menu toggle
    legendContent.querySelectorAll('.legend-actions-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const dropdown = btn.closest('.legend-actions-dropdown');
            const menu = dropdown.querySelector('.legend-actions-menu');

            // Close all other menus
            document.querySelectorAll('.legend-actions-menu.show').forEach(m => {
                if (m !== menu) m.classList.remove('show');
            });

            // Toggle this menu
            menu.classList.toggle('show');

            // Position the menu
            const btnRect = btn.getBoundingClientRect();
            menu.style.position = 'fixed';
            menu.style.top = `${btnRect.bottom + 4}px`;
            menu.style.right = `${window.innerWidth - btnRect.right}px`;
        });
    });

    // Info action
    legendContent.querySelectorAll('.legend-action-info').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const layerId = btn.dataset.layerId;
            showLayerInfo(layerId);
            // Close menu
            btn.closest('.legend-actions-menu').classList.remove('show');
        });
    });

    // Open Table action
    legendContent.querySelectorAll('.legend-action-table').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const layerId = btn.dataset.layerId;
            const layer = getLayer(layerId);

            if (layer) {
                // Show the table if it's hidden
                if (!attributeTableContainer.classList.contains('visible')) {
                    showTable();
                }

                // Switch to this layer's tab
                switchToLayerTab(layerId);

                // Close menu
                btn.closest('.legend-actions-menu').classList.remove('show');

                showStatus(`Viewing ${layer.name} attributes`, 'info');
            }
        });
    });

    // Remove action
    legendContent.querySelectorAll('.legend-action-remove').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const layerId = btn.dataset.layerId;
            removeLayer(layerId);
            updateLegend();
        });
    });

    // Edit toggle button - toggles edit mode on/off
    legendContent.querySelectorAll('.legend-edit-toggle-btn').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const layerId = btn.dataset.layerId;

            // Toggle edit mode
            if (isEditMode() && getCurrentEditLayerId() === layerId) {
                // Already editing this layer - save and turn off
                toggleEditMode(false);
                updateLegend();

                // Show information about data storage and export (only once per session)
                if (!hasShownSaveAlert) {
                    await showAlert(
                        'Changes Saved Locally',
                        'Your edits have been saved to your browser\'s local storage.\n\n' +
                        '⚠️ Important: These changes exist only in your browser and will be lost if you clear your browser data or use a different device.\n\n' +
                        '💾 To preserve your work permanently, export the layer using the download menu (⋮ → Export).\n\n' +
                        '🔒 Privacy Note: No data is uploaded to any server. All changes remain private on your device.'
                    );
                    hasShownSaveAlert = true;
                }

                showStatus('Changes saved to browser storage', 'success');
            } else {
                // Enable edit mode for this layer
                const success = toggleEditMode(true, layerId);
                if (success) {
                    updateLegend();
                    showStatus('Edit mode enabled - Click features to edit', 'info');
                }
            }
        });
    });

    // Legend edit bar buttons removed - now using edit controls popup at top of map

    // Download action - show submenu
    legendContent.querySelectorAll('.legend-action-download').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const layerId = btn.dataset.layerId;
            const dropdown = btn.closest('.legend-actions-dropdown');
            const submenu = dropdown.querySelector('.legend-download-submenu');

            // Toggle submenu
            submenu.classList.toggle('show');

            // Position submenu
            const btnRect = btn.getBoundingClientRect();
            submenu.style.position = 'fixed';

            // On mobile, position below; on desktop, position to the right
            const isMobile = window.innerWidth <= 768;
            if (isMobile) {
                submenu.style.top = `${btnRect.bottom + 4}px`;
                submenu.style.left = `${btnRect.left}px`;
            } else {
                submenu.style.top = `${btnRect.top}px`;
                submenu.style.left = `${btnRect.right + 4}px`;
            }
        });
    });

    // Download format selection - show projection modal
    legendContent.querySelectorAll('.legend-download-option').forEach(option => {
        option.addEventListener('click', async (e) => {
            e.stopPropagation();
            const format = option.dataset.format;
            const submenu = option.closest('.legend-download-submenu');
            const layerId = submenu.dataset.layerId;
            const layer = getLayer(layerId);

            // Close dropdown and submenu
            submenu.classList.remove('show');
            const actionsMenu = submenu.closest('.legend-actions-dropdown')?.querySelector('.legend-actions-menu');
            if (actionsMenu) actionsMenu.classList.remove('show');

            if (!layer) {
                showStatus('Layer not found', 'error');
                return;
            }

            // Determine if projection selection is needed for this format
            let selectedProjection;

            if (format === 'kml' || format === 'csv') {
                // KML and CSV only support WGS84, skip projection modal
                selectedProjection = {
                    projection: 'EPSG:4326',
                    projectionName: 'WGS84'
                };
            } else {
                // Shapefile and GeoJSON support multiple projections
                // Show projection modal and wait for user selection
                selectedProjection = await showProjectionModal(layer);

                // If user cancelled, don't export
                if (!selectedProjection) {
                    return;
                }
            }

            try {
                const formatNames = {
                    'shp': 'Shapefile',
                    'geojson': 'GeoJSON',
                    'kml': 'KML',
                    'csv': 'CSV'
                };
                showStatus(`Preparing ${formatNames[format]} export...`, 'info');

                // Use layer.geojson as the authoritative source (contains all edits: table + geometry)
                // layer.geojson is always kept in sync by layerManager event handlers
                const currentGeoJSON = layer.geojson;

                console.log('=== EXPORT DEBUG ===');
                console.log('Layer ID:', layerId);
                console.log('Layer name:', layer.name);
                console.log('Export GeoJSON features:', currentGeoJSON?.features?.length || 0);
                console.log('Export format:', format);

                await exportLayer(
                    currentGeoJSON,
                    layer.name,
                    format,
                    layer.metadata,
                    selectedProjection.projection,
                    selectedProjection.projectionName
                );
                showStatus(`${formatNames[format]} exported successfully`, 'success');
            } catch (error) {
                console.error(`Error exporting ${format}:`, error);
                showStatus(`Error exporting: ${error.message}`, 'error');
            }
        });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
        if (!e.target.closest('.legend-actions-dropdown')) {
            document.querySelectorAll('.legend-actions-menu.show').forEach(menu => {
                menu.classList.remove('show');
            });
            document.querySelectorAll('.legend-download-submenu.show').forEach(submenu => {
                submenu.classList.remove('show');
            });
        }
    });

    // Add drag-and-drop functionality for reordering layers
    let draggedElement = null;

    legendContent.querySelectorAll('.legend-layer').forEach(layerEl => {
        layerEl.addEventListener('dragstart', (e) => {
            draggedElement = layerEl;
            layerEl.classList.add('dragging');
            e.dataTransfer.effectAllowed = 'move';
        });

        layerEl.addEventListener('dragend', (e) => {
            layerEl.classList.remove('dragging');
            // Remove all drag-over classes
            legendContent.querySelectorAll('.legend-layer').forEach(el => {
                el.classList.remove('drag-over');
            });
        });

        layerEl.addEventListener('dragover', (e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';

            if (draggedElement && draggedElement !== layerEl) {
                // Add visual feedback
                layerEl.classList.add('drag-over');
            }
        });

        layerEl.addEventListener('dragleave', (e) => {
            layerEl.classList.remove('drag-over');
        });

        layerEl.addEventListener('drop', (e) => {
            e.preventDefault();
            layerEl.classList.remove('drag-over');

            if (draggedElement && draggedElement !== layerEl) {
                // Get the IDs of the dragged and target layers
                const draggedId = draggedElement.dataset.layerId;
                const targetId = layerEl.dataset.layerId;

                // Reorder layers in the data structure
                reorderLayers(draggedId, targetId);

                // Update legend to reflect new order
                updateLegend();
            }
        });
    });

    // Preview button functionality
    // Preview action button handler
    legendContent.querySelectorAll('.legend-action-preview').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const layerId = btn.dataset.layerId;
            const actionsMenu = btn.closest('.legend-actions-menu');
            if (actionsMenu) actionsMenu.classList.remove('show');
            startPreviewMode(layerId);
        });
    });

    // Legend tab is always visible now
    // legendTabBtn.style.display = 'block';
    switchTab('legend-tab');
}

function showLayerInfo(layerId) {
    const layer = getLayer(layerId);
    if (!layer) return;

    const { geometryTypes, propertyKeys, bounds, crs } = layer.metadata;
    const layerName = layer.name.replace(/\.zip$/i, '');

    // Build modal content (same as before)
    const modalContent = buildLayerInfoContent(layer.metadata, layer.featureCount, geometryTypes);

    modalLayerInfo.innerHTML = modalContent;
    modalLayerName.textContent = layerName;
    layerInfoModal.style.display = 'flex';
}

function buildLayerInfoContent(metadata, featureCount, geometryTypes) {
    const { propertyKeys, bounds, originalBounds, crs, originalProjection } = metadata;

    return `
        <div class="modal-info-section">
            <div class="modal-info-section-title">General Information</div>
            <div class="modal-info-grid">
                <div class="modal-info-item">
                    <span class="modal-info-label">Features</span>
                    <span class="modal-info-value">${featureCount.toLocaleString()}</span>
                </div>
                <div class="modal-info-item">
                    <span class="modal-info-label">Geometry Type</span>
                    <span class="modal-info-value">${geometryTypes.join(', ')}</span>
                </div>
                ${originalProjection ? `
                <div class="modal-info-item" style="grid-column: 1 / -1;">
                    <span class="modal-info-label">Projection</span>
                    <span class="modal-info-value">${originalProjection}</span>
                </div>
                ` : ''}
            </div>
        </div>
        ${(originalBounds || bounds) ? `
        <div class="modal-info-section">
            <div class="modal-info-section-title">Extent${originalProjection ? ` (${originalProjection})` : ''}</div>
            <div class="modal-info-grid">
                <div class="modal-info-item">
                    <span class="modal-info-label">Min X</span>
                    <span class="modal-info-value">${(originalBounds || bounds).minLon.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
                </div>
                <div class="modal-info-item">
                    <span class="modal-info-label">Max X</span>
                    <span class="modal-info-value">${(originalBounds || bounds).maxLon.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
                </div>
                <div class="modal-info-item">
                    <span class="modal-info-label">Min Y</span>
                    <span class="modal-info-value">${(originalBounds || bounds).minLat.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
                </div>
                <div class="modal-info-item">
                    <span class="modal-info-label">Max Y</span>
                    <span class="modal-info-value">${(originalBounds || bounds).maxLat.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
                </div>
            </div>
        </div>
        ` : ''}
        ${propertyKeys && propertyKeys.length > 0 ? `
        <div class="modal-info-section">
            <div class="modal-info-section-title">Attributes (${propertyKeys.length})</div>
            <div class="modal-attributes">
                ${propertyKeys.map(key => `<span class="modal-attribute-tag">${key}</span>`).join('')}
            </div>
        </div>
        ` : ''}
    `;
}

/**
 * Convert hex color to RGB
 */
function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '0, 0, 0';
}

/**
 * Toggle preview mode for a layer
 * @param {string} layerId - The layer ID to preview
 * @param {HTMLElement} button - The preview button element
 */
function togglePreviewMode(layerId, button) {
    const layer = getLayer(layerId);
    if (!layer) return;

    // If preview is already active for this layer, stop it
    if (previewState.isActive && previewState.layerId === layerId) {
        stopPreview(button);
    } else {
        // Stop any existing preview first
        if (previewState.isActive) {
            stopPreview();
        }
        // Start preview for this layer
        startPreview(layerId, layer, button);
    }
}

/**
 * Start preview mode
 * @param {string} layerId - The layer ID
 * @param {Object} layer - The layer object
 * @param {HTMLElement} button - The preview button element
 */
function startPreview(layerId, layer, button) {
    // Make sure the layer's tab is active
    switchToLayerTab(layerId);

    // Show the attribute table if it's hidden
    if (attributeTableContainer.classList.contains('hidden')) {
        showTable();
    }

    // Initialize preview state
    previewState.isActive = true;
    previewState.layerId = layerId;
    previewState.currentIndex = 0;

    // Update button appearance
    const playIcon = button.querySelector('.play-icon');
    const pauseIcon = button.querySelector('.pause-icon');
    const label = button.querySelector('.preview-label');

    if (playIcon) playIcon.style.display = 'none';
    if (pauseIcon) pauseIcon.style.display = 'block';
    if (label) label.textContent = 'Stop';
    button.classList.add('active');

    // Start cycling through features
    cycleToNextFeature(layer);
    previewState.intervalId = setInterval(() => {
        cycleToNextFeature(layer);
    }, previewState.delay);
}

/**
 * Stop preview mode
 * @param {HTMLElement} button - The preview button element (optional)
 */
function stopPreview(button) {
    // Clear interval
    if (previewState.intervalId) {
        clearInterval(previewState.intervalId);
        previewState.intervalId = null;
    }

    // Reset state
    previewState.isActive = false;
    const stoppedLayerId = previewState.layerId;
    previewState.layerId = null;
    previewState.currentIndex = 0;

    // Clear highlights
    clearRowHighlight();
    removeHighlight();

    // Update button appearance
    if (button) {
        updatePreviewButton(button, false);
    } else if (stoppedLayerId) {
        // Find the button for the stopped layer
        const stoppedButton = document.querySelector(`.legend-preview-btn[data-layer-id="${stoppedLayerId}"]`);
        if (stoppedButton) {
            updatePreviewButton(stoppedButton, false);
        }
    }
}

/**
 * Update preview button appearance
 * @param {HTMLElement} button - The preview button element
 * @param {boolean} isActive - Whether preview is active
 */
function updatePreviewButton(button, isActive) {
    const playIcon = button.querySelector('.play-icon');
    const pauseIcon = button.querySelector('.pause-icon');
    const label = button.querySelector('.preview-label');

    if (isActive) {
        if (playIcon) playIcon.style.display = 'none';
        if (pauseIcon) pauseIcon.style.display = 'block';
        if (label) label.textContent = 'Stop';
        button.classList.add('active');
    } else {
        if (playIcon) playIcon.style.display = 'block';
        if (pauseIcon) pauseIcon.style.display = 'none';
        if (label) label.textContent = 'Preview';
        button.classList.remove('active');
    }
}

/**
 * Start preview mode for a layer (new UI with overlay)
 * @param {string} layerId - The layer ID
 */
function startPreviewMode(layerId) {
    const layer = getLayer(layerId);
    if (!layer) return;

    // Stop any existing preview first
    if (previewState.isActive) {
        stopPreviewMode();
    }

    // Make sure the layer's tab is active
    switchToLayerTab(layerId);

    // Show the attribute table if it's hidden
    if (attributeTableContainer.classList.contains('hidden')) {
        showTable();
    }

    // Initialize preview state
    previewState.isActive = true;
    previewState.layerId = layerId;
    previewState.currentIndex = 0;
    previewState.isPaused = false;

    // Show and update the preview overlay
    previewPlayOverlay.style.display = 'flex';
    updatePreviewOverlay(false); // false = playing

    // Start cycling through features
    cycleToNextFeature(layer);
    previewState.intervalId = setInterval(() => {
        cycleToNextFeature(layer);
    }, previewState.delay);
}

/**
 * Toggle play/pause for preview mode
 */
function togglePreviewPlayPause() {
    if (!previewState.isActive) return;

    const layer = getLayer(previewState.layerId);
    if (!layer) return;

    if (previewState.isPaused) {
        // Resume preview
        previewState.isPaused = false;
        updatePreviewOverlay(false);

        // Start cycling again
        cycleToNextFeature(layer);
        previewState.intervalId = setInterval(() => {
            cycleToNextFeature(layer);
        }, previewState.delay);
    } else {
        // Pause preview
        previewState.isPaused = true;
        updatePreviewOverlay(true);

        // Stop the interval
        if (previewState.intervalId) {
            clearInterval(previewState.intervalId);
            previewState.intervalId = null;
        }
    }
}

/**
 * Stop preview mode completely
 */
function stopPreviewMode() {
    // Clear interval
    if (previewState.intervalId) {
        clearInterval(previewState.intervalId);
        previewState.intervalId = null;
    }

    // Reset state
    previewState.isActive = false;
    previewState.layerId = null;
    previewState.currentIndex = 0;
    previewState.isPaused = false;

    // Hide the preview overlay
    previewPlayOverlay.style.display = 'none';

    // Clear highlights
    clearRowHighlight();
    removeHighlight();
}

/**
 * Update preview overlay button appearance
 * @param {boolean} isPaused - Whether preview is paused
 */
function updatePreviewOverlay(isPaused) {
    const playIcon = previewPlayBtn.querySelector('.play-icon');
    const pauseIcon = previewPlayBtn.querySelector('.pause-icon');

    if (isPaused) {
        if (playIcon) playIcon.style.display = 'block';
        if (pauseIcon) pauseIcon.style.display = 'none';
    } else {
        if (playIcon) playIcon.style.display = 'none';
        if (pauseIcon) pauseIcon.style.display = 'block';
    }
}

/**
 * Go to next feature in preview
 */
function previewNextFeature() {
    if (!previewState.isActive) return;

    const layer = getLayer(previewState.layerId);
    if (!layer) return;

    const features = layer.geojson.features;
    if (!features || features.length === 0) return;

    // currentIndex already points to NEXT feature, so just show it
    const feature = features[previewState.currentIndex];
    highlightAndZoomToFeature(layer, feature, previewState.currentIndex);

    // Increment index so it points to next feature (maintaining the pattern)
    previewState.currentIndex = (previewState.currentIndex + 1) % features.length;

    // If currently playing, reset the interval
    if (!previewState.isPaused && previewState.intervalId) {
        clearInterval(previewState.intervalId);
        previewState.intervalId = setInterval(() => {
            cycleToNextFeature(layer);
        }, previewState.delay);
    }
}

/**
 * Go to previous feature in preview
 */
function previewPreviousFeature() {
    if (!previewState.isActive) return;

    const layer = getLayer(previewState.layerId);
    if (!layer) return;

    const features = layer.geojson.features;
    if (!features || features.length === 0) return;

    // currentIndex points to NEXT feature, so go back 2 to get previous
    // (1 to get to current, 1 to go back from there)
    previewState.currentIndex = previewState.currentIndex - 2;
    if (previewState.currentIndex < 0) {
        previewState.currentIndex = features.length + previewState.currentIndex;
    }

    // Show the feature
    const feature = features[previewState.currentIndex];
    highlightAndZoomToFeature(layer, feature, previewState.currentIndex);

    // Increment index so it points to next feature (maintaining the pattern)
    previewState.currentIndex = (previewState.currentIndex + 1) % features.length;

    // If currently playing, reset the interval
    if (!previewState.isPaused && previewState.intervalId) {
        clearInterval(previewState.intervalId);
        previewState.intervalId = setInterval(() => {
            cycleToNextFeature(layer);
        }, previewState.delay);
    }
}

/**
 * Highlight and zoom to a specific feature
 * @param {Object} layer - The layer object
 * @param {Object} feature - The feature to highlight
 * @param {number} index - The feature index
 */
function highlightAndZoomToFeature(layer, feature, index) {
    // Highlight the row in the table with scroll
    highlightRow(index, true);

    // Highlight the feature on the map
    highlightFeature(feature, true);
}

/**
 * Cycle to the next feature in preview mode
 * @param {Object} layer - The layer object
 */
function cycleToNextFeature(layer) {
    if (!layer || !layer.geojson || !layer.geojson.features) return;

    const features = layer.geojson.features;
    if (features.length === 0) return;

    // Get the current feature
    const feature = features[previewState.currentIndex];
    if (!feature) return;

    // Highlight the row in the table
    highlightRow(previewState.currentIndex, true);

    // Highlight the feature on the map
    highlightFeature(feature, true);

    // Move to next index (loop back to 0 if at end)
    previewState.currentIndex = (previewState.currentIndex + 1) % features.length;
}

function displayLegend(fileName, metadata, featureCount) {
    const { geometryTypes, propertyKeys, bounds, originalBounds, crs, originalProjection } = metadata;

    // Remove .zip extension for display
    const layerName = fileName.replace(/\.zip$/i, '');

    // Determine primary geometry type
    const primaryGeometry = geometryTypes[0] || 'Unknown';
    let geometryIcon = 'polygon';
    let geometryLabel = primaryGeometry;

    if (primaryGeometry.includes('Point')) {
        geometryIcon = 'point';
        geometryLabel = 'Point Features';
    } else if (primaryGeometry.includes('Line')) {
        geometryIcon = 'line';
        geometryLabel = 'Line Features';
    } else if (primaryGeometry.includes('Polygon')) {
        geometryIcon = 'polygon';
        geometryLabel = 'Polygon Features';
    }

    // Build bounds info
    let boundsHtml = '';
    if (bounds) {
        boundsHtml = `
            <div class="legend-info-section">
                <div class="legend-info-section-title">Extent</div>
                <div class="legend-info-grid">
                    <div class="legend-info-item">
                        <span class="legend-info-label">Min Lon:</span>
                        <span class="legend-info-value">${bounds.minLon.toFixed(6)}</span>
                    </div>
                    <div class="legend-info-item">
                        <span class="legend-info-label">Max Lon:</span>
                        <span class="legend-info-value">${bounds.maxLon.toFixed(6)}</span>
                    </div>
                    <div class="legend-info-item">
                        <span class="legend-info-label">Min Lat:</span>
                        <span class="legend-info-value">${bounds.minLat.toFixed(6)}</span>
                    </div>
                    <div class="legend-info-item">
                        <span class="legend-info-label">Max Lat:</span>
                        <span class="legend-info-value">${bounds.maxLat.toFixed(6)}</span>
                    </div>
                </div>
            </div>
        `;
    }

    // Build attributes info
    let attributesHtml = '';
    if (propertyKeys && propertyKeys.length > 0) {
        attributesHtml = `
            <div class="legend-info-section">
                <div class="legend-info-section-title">Attributes (${propertyKeys.length})</div>
                <div class="legend-attributes">
                    ${propertyKeys.map(key => `<span class="legend-attribute-tag">${key}</span>`).join('')}
                </div>
            </div>
        `;
    }

    // Build modal content
    const modalContent = `
        <div class="modal-info-section">
            <div class="modal-info-section-title">General Information</div>
            <div class="modal-info-grid">
                <div class="modal-info-item">
                    <span class="modal-info-label">Features</span>
                    <span class="modal-info-value">${featureCount.toLocaleString()}</span>
                </div>
                <div class="modal-info-item">
                    <span class="modal-info-label">Geometry Type</span>
                    <span class="modal-info-value">${geometryTypes.join(', ')}</span>
                </div>
                ${originalProjection ? `
                <div class="modal-info-item" style="grid-column: 1 / -1;">
                    <span class="modal-info-label">Projection</span>
                    <span class="modal-info-value">${originalProjection}</span>
                </div>
                ` : ''}
            </div>
        </div>
        ${(originalBounds || bounds) ? `
        <div class="modal-info-section">
            <div class="modal-info-section-title">Extent${originalProjection ? ` (${originalProjection})` : ''}</div>
            <div class="modal-info-grid">
                <div class="modal-info-item">
                    <span class="modal-info-label">Min X</span>
                    <span class="modal-info-value">${(originalBounds || bounds).minLon.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
                </div>
                <div class="modal-info-item">
                    <span class="modal-info-label">Max X</span>
                    <span class="modal-info-value">${(originalBounds || bounds).maxLon.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
                </div>
                <div class="modal-info-item">
                    <span class="modal-info-label">Min Y</span>
                    <span class="modal-info-value">${(originalBounds || bounds).minLat.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
                </div>
                <div class="modal-info-item">
                    <span class="modal-info-label">Max Y</span>
                    <span class="modal-info-value">${(originalBounds || bounds).maxLat.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
                </div>
            </div>
        </div>
        ` : ''}
        ${propertyKeys && propertyKeys.length > 0 ? `
        <div class="modal-info-section">
            <div class="modal-info-section-title">Attributes (${propertyKeys.length})</div>
            <div class="modal-attributes">
                ${propertyKeys.map(key => `<span class="modal-attribute-tag">${key}</span>`).join('')}
            </div>
        </div>
        ` : ''}
    `;

    const html = `
        <div class="legend-layer">
            <div class="legend-layer-name">
                <span>${layerName}</span>
                <button class="legend-info-btn" title="Show layer information">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.5"/>
                        <text x="8" y="12" text-anchor="middle" font-size="11" font-weight="600">i</text>
                    </svg>
                </button>
            </div>
            <div class="legend-symbol">
                <div class="legend-symbol-icon ${geometryIcon}"></div>
                <span class="legend-symbol-label">${geometryLabel}</span>
            </div>
        </div>
    `;

    legendContent.innerHTML = html;

    // Store modal content for later
    modalLayerInfo.innerHTML = modalContent;
    modalLayerName.textContent = layerName;

    // Add info button click handler to show modal
    const infoBtn = legendContent.querySelector('.legend-info-btn');
    if (infoBtn) {
        infoBtn.addEventListener('click', () => {
            layerInfoModal.style.display = 'flex';
        });
    }

    // Legend tab is always visible now
    // legendTabBtn.style.display = 'block';
    switchTab('legend-tab');
}

function highlightTableRow(index, shouldZoom = true, shouldScroll = false) {
    const feature = getFeature(index);
    if (!feature) return;

    // In edit mode, load the feature for editing and zoom to it
    if (isEditMode()) {
        // Get the current layer to access the source
        const currentLayerObj = getCurrentLayer();
        if (currentLayerObj && currentLayerObj.geojson && currentLayerObj.geojson.features) {
            const originalFeature = currentLayerObj.geojson.features[index];
            if (originalFeature) {
                // Dispatch event to load feature for editing
                const event = new CustomEvent('loadFeatureForEdit', {
                    detail: { feature: originalFeature, shouldZoom }
                });
                document.dispatchEvent(event);

                // Still highlight the row in the table
                highlightRow(index, shouldScroll);
            }
        }
        return;
    }

    // Don't auto-open table when clicking points - let user control table visibility
    // Only highlight row if table is already open
    if (!attributeTableContainer.classList.contains('hidden')) {
        // Highlight table row (using module) - only scroll if explicitly requested
        highlightRow(index, shouldScroll);
    }

    // Highlight on map
    if (feature) {
        highlightFeature(feature, shouldZoom);
    }
}

async function loadHistory() {
    // History is now accessed via the "Load from Memory" option in the browse dropdown
    // Update the memory option visibility
    await updateMemoryOptionVisibility();
}

async function loadFromHistory(id) {
    try {
        showStatus('Loading from history...', 'info');

        const data = await getShapefile(id);
        if (!data) {
            showStatus('File not found in history', 'error');
            return;
        }

        // Add layer from history
        const layerId = addLayer({
            name: data.name,
            geojson: data.geojson,
            metadata: data.metadata,
            featureCount: data.geojson.features?.length || 0,
            dbId: id // Store the database ID
        }, (index) => {
            // When feature clicked on map, highlight without zoom
            highlightTableRow(index, false);
        });

        // Update UI
        updateLegend();

        showStatus(`Loaded ${data.name} from history`, 'success');
    } catch (error) {
        console.error('Error loading from history:', error);
        showStatus(`Error loading from history: ${error.message}`, 'error');
    }
}

async function clearHistory() {
    try {
        await clearAllShapefiles();
        await loadHistory();

        // Remove all layers
        removeAllLayers();

        removeHighlight();
        // legendTabBtn.style.display = 'none'; // Legend tab is always visible now
        attributeTableContainer.classList.remove('visible');
        mapContainer.classList.remove('table-visible');

        // Stay on legend tab (no upload tab anymore)
        // switchTab('upload-tab');

        // Resize map after layout change
        setTimeout(() => {
            resizeMap();
        }, 350);

        showStatus('History cleared', 'success');
    } catch (error) {
        console.error('Error clearing history:', error);
        showStatus(`Error clearing history: ${error.message}`, 'error');
    }
}

async function updateMemoryOptionVisibility() {
    try {
        const metadata = await getShapefileMetadata(getUserId());
        if (metadata && metadata.length > 0) {
            loadFromMemoryOption.style.display = 'flex';
        } else {
            loadFromMemoryOption.style.display = 'none';
        }
    } catch (error) {
        // Check if this is the missing index error (database needs upgrade)
        if (error.name === 'NotFoundError' && error.message.includes('index')) {
            console.warn('Database schema outdated - resetting to v2...');
            try {
                await resetDatabase();
                console.log('Database reset complete - reloading page...');
                location.reload();
            } catch (resetError) {
                console.error('Failed to reset database:', resetError);
                showStatus('Database upgrade required. Please refresh the page.', 'error');
            }
        } else {
            console.error('Error checking memory:', error);
        }
        loadFromMemoryOption.style.display = 'none';
    }
}

async function toggleBrowseDropdown() {
    const isVisible = browseDropdownMenu.classList.contains('show');
    if (isVisible) {
        closeBrowseDropdown();
    } else {
        // Update memory option visibility before showing menu
        await updateMemoryOptionVisibility();

        // Position the dropdown below the button
        const rect = browseBtn.getBoundingClientRect();
        browseDropdownMenu.style.top = `${rect.bottom + 5}px`;
        browseDropdownMenu.style.right = `${window.innerWidth - rect.right}px`;
        browseDropdownMenu.classList.add('show');
    }
}

function closeBrowseDropdown() {
    browseDropdownMenu.classList.remove('show');
}

/**
 * Create a new empty layer
 */
async function createNewLayer() {
    return new Promise((resolve) => {
        const modal = document.getElementById('create-layer-modal');
        const layerNameInput = document.getElementById('layer-name-input');
        const confirmBtn = document.getElementById('confirm-create-layer-btn');
        const cancelBtn = document.getElementById('cancel-create-layer-btn');
        const closeBtn = document.getElementById('close-create-layer-btn');
        const addFieldBtn = document.getElementById('add-field-to-layer-btn');
        const fieldsList = document.getElementById('create-layer-fields-list');

        // Track fields to be created
        let fieldsToCreate = [];

        // Reset form
        layerNameInput.value = 'New Layer';
        layerNameInput.select();
        document.querySelector('input[name="geometry-type"][value="Point"]').checked = true;
        fieldsToCreate = [];
        fieldsList.innerHTML = '';

        // Show modal
        modal.style.display = 'flex';

        // Add field handler
        const addField = () => {
            // Check if form already exists
            if (fieldsList.querySelector('.create-field-form')) return;

            // Create inline form
            const formDiv = document.createElement('div');
            formDiv.className = 'create-field-form';
            formDiv.innerHTML = `
                <div class="create-field-form-content">
                    <div class="form-row">
                        <input type="text" class="field-form-input" placeholder="Field name" maxlength="10" id="temp-field-name" />
                        <small>Max 10 characters</small>
                    </div>
                    <div class="form-row">
                        <div class="field-form-type-btns">
                            <button type="button" class="field-form-type-btn active" data-type="text">Text</button>
                            <button type="button" class="field-form-type-btn" data-type="number">#</button>
                            <button type="button" class="field-form-type-btn" data-type="date">Date</button>
                        </div>
                    </div>
                    <div class="form-row">
                        <input type="text" class="field-form-input" placeholder="Default value (optional)" id="temp-field-default" />
                    </div>
                    <div class="form-row form-actions">
                        <button type="button" class="field-form-btn field-form-cancel">Cancel</button>
                        <button type="button" class="field-form-btn field-form-add">Add</button>
                    </div>
                </div>
            `;

            fieldsList.insertBefore(formDiv, fieldsList.firstChild);

            const nameInput = formDiv.querySelector('#temp-field-name');
            const defaultInput = formDiv.querySelector('#temp-field-default');
            const typeBtns = formDiv.querySelectorAll('.field-form-type-btn');
            const cancelBtn = formDiv.querySelector('.field-form-cancel');
            const addBtn = formDiv.querySelector('.field-form-add');

            // Type button handlers
            typeBtns.forEach(btn => {
                btn.addEventListener('click', () => {
                    typeBtns.forEach(b => b.classList.remove('active'));
                    btn.classList.add('active');
                });
            });

            // Cancel handler
            const removeForm = () => {
                formDiv.remove();
            };

            cancelBtn.onclick = removeForm;

            // Add handler
            addBtn.onclick = () => {
                const fieldName = nameInput.value.trim();
                const fieldType = formDiv.querySelector('.field-form-type-btn.active').dataset.type;
                const defaultValue = defaultInput.value.trim();

                // Validate
                if (!fieldName) {
                    alert('Please enter a field name');
                    nameInput.focus();
                    return;
                }

                if (fieldsToCreate.some(f => f.name === fieldName)) {
                    alert(`Field "${fieldName}" already exists`);
                    nameInput.focus();
                    return;
                }

                // Add field
                fieldsToCreate.push({
                    name: fieldName,
                    type: fieldType,
                    defaultValue: defaultValue || null
                });
                removeForm();
                renderFieldsList();
            };

            // Enter key handler
            nameInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    addBtn.click();
                } else if (e.key === 'Escape') {
                    removeForm();
                }
            });

            nameInput.focus();
        };

        // Render fields list
        const renderFieldsList = () => {
            fieldsList.innerHTML = '';
            fieldsToCreate.forEach((field, index) => {
                const fieldItem = document.createElement('div');
                fieldItem.className = 'create-layer-field-item';
                fieldItem.innerHTML = `
                    <span class="field-item-name">${field.name}</span>
                    <span class="field-item-type">${field.type}</span>
                    <button type="button" class="field-item-delete" data-index="${index}" title="Remove field">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                `;

                // Delete handler
                fieldItem.querySelector('.field-item-delete').onclick = () => {
                    fieldsToCreate.splice(index, 1);
                    renderFieldsList();
                };

                fieldsList.appendChild(fieldItem);
            });
        };

        // Add field button click
        addFieldBtn.onclick = addField;

        const closeModal = () => {
            modal.style.display = 'none';
            resolve(null);
        };

        const createLayer = async () => {
            const layerName = layerNameInput.value.trim();
            if (!layerName) {
                showStatus('Please enter a layer name', 'error');
                return;
            }

            const geometryType = document.querySelector('input[name="geometry-type"]:checked').value;

            // Create empty GeoJSON
            const emptyGeoJSON = {
                type: 'FeatureCollection',
                features: []
            };

            // Build default fields from fieldsToCreate
            const defaultFields = {};
            const fieldTypes = {};

            fieldsToCreate.forEach(field => {
                // Use provided default value or set based on type
                let defaultValue = field.defaultValue;

                if (!defaultValue) {
                    if (field.type === 'number') {
                        defaultValue = 0;
                    } else {
                        defaultValue = '';
                    }
                } else if (field.type === 'number') {
                    defaultValue = Number(defaultValue);
                }

                defaultFields[field.name] = defaultValue;
                fieldTypes[field.name] = field.type;
            });

            // Create metadata
            const metadata = {
                geometryTypes: [geometryType],
                geometryType: geometryType,
                createdAt: new Date().toISOString(),
                source: 'created',
                defaultFields: defaultFields,
                fieldTypes: fieldTypes
            };

            try {
                // Save to IndexedDB first
                const dbId = await saveShapefile({
                    name: layerName,
                    geojson: emptyGeoJSON,
                    metadata: metadata
                }, getUserId());

                // Add to map
                const layerData = {
                    name: layerName,
                    geojson: emptyGeoJSON,
                    metadata: metadata,
                    featureCount: 0,
                    dbId: dbId
                };

                addLayer(layerData);
                updateLegend();

                showStatus(`Layer "${layerName}" created successfully`, 'success');

                // Hide empty map overlay if visible
                if (emptyMapOverlay) {
                    emptyMapOverlay.style.display = 'none';
                }

                closeModal();
                resolve(layerData);

            } catch (error) {
                console.error('Error creating layer:', error);
                showStatus('Failed to create layer', 'error');
            }
        };

        // Event listeners
        confirmBtn.onclick = createLayer;
        cancelBtn.onclick = closeModal;
        closeBtn.onclick = closeModal;

        // Handle Enter key
        layerNameInput.onkeydown = (e) => {
            if (e.key === 'Enter') {
                createLayer();
            } else if (e.key === 'Escape') {
                closeModal();
            }
        };

        // Close on overlay click
        modal.querySelector('.modal-overlay').onclick = closeModal;

        // Prevent closing when clicking modal content
        modal.querySelector('.modal-content').onclick = (e) => e.stopPropagation();
    });
}

/**
 * Show geometry type selection dialog
 */
async function showGeometryTypeDialog(types) {
    return new Promise((resolve) => {
        const modal = document.createElement('div');
        modal.className = 'modal-overlay';
        modal.style.display = 'flex';

        modal.innerHTML = `
            <div class="modal-content" style="max-width: 400px;">
                <div class="modal-header">
                    <h3>Select Geometry Type</h3>
                    <button class="close-btn" id="close-geometry-dialog">&times;</button>
                </div>
                <div class="modal-body">
                    <div style="display: flex; flex-direction: column; gap: 12px;">
                        ${types.map(type => `
                            <button class="geometry-type-btn" data-value="${type.value}" style="
                                padding: 16px;
                                background: var(--color-bg-secondary);
                                border: 2px solid var(--color-border);
                                border-radius: var(--radius-md);
                                cursor: pointer;
                                transition: all var(--transition-base);
                                text-align: left;
                            ">
                                <div style="font-weight: 600; color: var(--color-text-primary); margin-bottom: 4px;">
                                    ${type.label}
                                </div>
                                <div style="font-size: 12px; color: var(--color-text-muted);">
                                    ${type.description}
                                </div>
                            </button>
                        `).join('')}
                    </div>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        // Add hover styles
        const btns = modal.querySelectorAll('.geometry-type-btn');
        btns.forEach(btn => {
            btn.addEventListener('mouseenter', () => {
                btn.style.background = 'var(--color-bg-hover)';
                btn.style.borderColor = 'var(--color-primary)';
            });
            btn.addEventListener('mouseleave', () => {
                btn.style.background = 'var(--color-bg-secondary)';
                btn.style.borderColor = 'var(--color-border)';
            });
            btn.addEventListener('click', () => {
                const value = btn.getAttribute('data-value');
                document.body.removeChild(modal);
                resolve(value);
            });
        });

        // Close button
        modal.querySelector('#close-geometry-dialog').addEventListener('click', () => {
            document.body.removeChild(modal);
            resolve(null);
        });

        // Click outside to close
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                document.body.removeChild(modal);
                resolve(null);
            }
        });
    });
}

async function showMemoryModal() {
    await populateMemoryList();
    memoryModal.style.display = 'flex';
}

function closeMemoryModal() {
    memoryModal.style.display = 'none';
}

// Settings Modal Functions
function showSettingsModal() {
    // Load current provider
    const provider = AI_SERVICE.getProvider();
    aiProviderSelect.value = provider;

    // Load saved model
    const savedModel = AI_SERVICE.getModel();
    if (savedModel) {
        aiModelSelect.value = savedModel;
        modelSelectionGroup.style.display = 'block';
    } else {
        modelSelectionGroup.style.display = 'none';
        apiTestStatus.style.display = 'none';
    }

    settingsModal.style.display = 'flex';
}

function closeSettingsModal() {
    settingsModal.style.display = 'none';
}

// AI Generate Modal Functions
async function showAiGenerateModal() {
    // Check if model is configured
    const model = AI_SERVICE.getModel();
    if (!model) {
        await showAlert(
            'Model Required',
            'Please select an AI model in Settings before using AI generation.'
        );
        showSettingsModal();
        return;
    }

    // Reset modal state
    aiPromptInput.value = '';
    aiGenerationStatus.style.display = 'none';
    aiGenerationStatus.classList.remove('error', 'success');
    confirmAiGenerateBtn.disabled = false;

    aiGenerateModal.style.display = 'flex';
    aiPromptInput.focus();
}

function closeAiGenerateModal() {
    aiGenerateModal.style.display = 'none';
}

async function handleAiGenerate() {
    const prompt = aiPromptInput.value.trim();

    if (!prompt) {
        await showAlert('Prompt Required', 'Please describe the layer you want to create.');
        return;
    }

    const model = AI_SERVICE.getModel();
    if (!model) {
        await showAlert(
            'Model Required',
            'Please select an AI model in Settings.'
        );
        closeAiGenerateModal();
        showSettingsModal();
        return;
    }

    // Show loading state
    aiGenerationStatus.style.display = 'flex';
    aiGenerationStatus.classList.remove('error', 'success');
    aiStatusText.textContent = 'Generating layer with AI...';
    confirmAiGenerateBtn.disabled = true;

    try {
        // Generate layer using AI
        const aiLayerData = await AI_SERVICE.generateLayer(prompt);

        // Format for layer manager
        const formattedLayer = AI_SERVICE.formatLayerForManager(aiLayerData);

        // Save to IndexedDB first
        const dbId = await saveShapefile({
            name: formattedLayer.name,
            geojson: formattedLayer.geojson,
            metadata: formattedLayer.metadata
        }, getUserId());

        // Add the layer with dbId
        formattedLayer.dbId = dbId;
        const layerId = addLayer(formattedLayer);

        // Show success
        aiGenerationStatus.classList.add('success');
        aiStatusText.textContent = `Successfully created "${formattedLayer.name}" with ${formattedLayer.geojson.features.length} features!`;

        // Close modal after a brief delay
        setTimeout(() => {
            closeAiGenerateModal();
            showStatus(`AI generated layer "${formattedLayer.name}" added successfully`);
        }, 1500);

    } catch (error) {
        console.error('AI generation error:', error);

        // Show error
        aiGenerationStatus.classList.add('error');
        aiStatusText.textContent = error.message || 'Failed to generate layer. Please try again.';
        confirmAiGenerateBtn.disabled = false;
    }
}

async function populateMemoryList() {
    try {
        const metadata = await getShapefileMetadata(getUserId());

        if (!metadata || metadata.length === 0) {
            memoryList.innerHTML = `
                <div class="memory-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                    </svg>
                    <p>No files in memory</p>
                </div>
            `;
            return;
        }

        const html = metadata.map(item => {
            const date = new Date(item.timestamp).toLocaleDateString();
            const featureCount = item.featureCount || 'Unknown';

            return `
                <div class="memory-item" data-id="${item.id}">
                    <div class="memory-item-info" onclick="loadMemoryItem(${item.id})">
                        <div class="memory-item-name">${item.name}</div>
                        <div class="memory-item-meta">${featureCount} features • ${date}</div>
                    </div>
                    <div class="memory-item-actions">
                        <button class="memory-item-delete" onclick="deleteMemoryItem(event, ${item.id})" title="Delete">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                        </button>
                    </div>
                </div>
            `;
        }).join('');

        memoryList.innerHTML = html;
    } catch (error) {
        console.error('Error populating memory list:', error);
        memoryList.innerHTML = '<div class="memory-empty"><p>Error loading memory</p></div>';
    }
}

/**
 * Load a file from memory
 */
window.loadMemoryItem = async function(id) {
    closeMemoryModal();
    await loadFromHistory(id);
};

window.deleteMemoryItem = async function(event, id) {
    event.stopPropagation();

    const confirmed = await showConfirm(
        'Delete File',
        'Are you sure you want to delete this file from memory?'
    );

    if (confirmed) {
        try {
            // Remove from IndexedDB
            const db = await initDB();
            const tx = db.transaction('shapefiles', 'readwrite');
            const store = tx.objectStore('shapefiles');
            await store.delete(id);

            // Refresh the list
            await populateMemoryList();

            // Update memory option visibility
            await updateMemoryOptionVisibility();

            showStatus('File deleted from memory', 'success');
        } catch (error) {
            console.error('Error deleting file:', error);
            showStatus(`Error deleting file: ${error.message}`, 'error');
        }
    }
};

function showStatus(message, type = 'info') {
    statusDiv.textContent = message;
    statusDiv.className = `status ${type}`;
    statusDiv.style.display = 'block';

    if (type === 'success' || type === 'info') {
        setTimeout(() => {
            statusDiv.style.display = 'none';
        }, 5000);
    }
}

function showProgress(progress, message) {
    progressContainer.style.display = 'block';
    progressFill.style.width = `${progress}%`;
    progressText.textContent = message;
}

function hideProgress() {
    setTimeout(() => {
        progressContainer.style.display = 'none';
        progressFill.style.width = '0%';
        progressText.textContent = '';
    }, 1000);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
